import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Award,
  Boxes,
  Calendar,
  CheckCircle,
  Download,
  FileText,
  Inbox,
  LayoutDashboard,
  LogOut,
  Settings,
  Shield,
  Trophy,
  Upload,
  UserCircle,
  Users,
  XCircle,
  Plug,
  RefreshCw,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import { api, logout as apiLogout, getUser, API } from "@/lib/api";
import { Logo } from "@/components/SiteChrome";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RewardOptionsEditor from "@/components/admin/RewardOptionsEditor";
import ReferralProductsEditor from "@/components/admin/ReferralProductsEditor";
import VerifySocialPanel from "@/components/admin/VerifySocialPanel";
import AmbassadorSocialsEditor from "@/components/admin/AmbassadorSocialsEditor";
import AdminActivityFeed from "@/components/admin/AdminActivityFeed";
import PerkFields from "@/components/admin/PerkFields";
import AdminResourcesTab from "@/components/admin/AdminResourcesTab";

const CT = [
  ["instagram_post_original", "Instagram post (original)"],
  ["instagram_story_reshare", "Instagram story reshare"],
  ["linkedin_post_original", "LinkedIn post (original)"],
  ["linkedin_reshare", "LinkedIn reshare"],
  ["whatsapp_share", "WhatsApp share"],
  ["instagram_reel", "Instagram Reel (original)"],
  ["youtube_video", "YouTube video (original)"],
  ["ai_marketing_video", "AI marketing video (original)"],
  ["newsletter_mention", "Newsletter mention"],
  ["blog_post", "Blog post about NoobSync"],
  ["podcast_feature", "Podcast feature / mention"],
];

const CT_LABEL = Object.fromEntries(CT);

function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-500/10 text-amber-300 border-amber-500/40",
    accepted: "bg-[#A855F7]/10 text-[#A855F7] border-[#A855F7]/40",
    approved: "bg-[#A855F7]/10 text-[#A855F7] border-[#A855F7]/40",
    rejected: "bg-rose-500/10 text-rose-300 border-rose-500/40",
    active: "bg-[#A855F7]/10 text-[#A855F7] border-[#A855F7]/40",
    draft: "bg-zinc-700/30 text-zinc-300 border-zinc-700",
    completed: "bg-zinc-700/30 text-zinc-300 border-zinc-700",
  };
  return <span className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase ${map[status] || "border-zinc-800 text-zinc-400"}`}>{status}</span>;
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-zinc-500">Loading…</div>;
  const tiles = [
    { l: "Total applications", v: stats.total_applications, t: "stat-apps" },
    { l: "Pending review", v: stats.pending_applications, t: "stat-pending" },
    { l: "Accepted", v: stats.accepted_applications, t: "stat-accepted" },
    { l: "Active ambassadors", v: stats.active_ambassadors, t: "stat-amb" },
    { l: "Total points (cohort)", v: stats.total_points_this_cohort, t: "stat-points" },
    { l: "Content reviews pending", v: stats.pending_content_reviews, t: "stat-content" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map((t) => (
          <div key={t.l} className="rounded-2xl border border-zinc-900 bg-zinc-950 p-5" data-testid={t.t}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t.l}</p>
            <p className="mt-2 font-display text-3xl font-black text-white">{t.v}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-lg font-bold">Current cohort</h3>
        {stats.current_cohort ? (
          <div className="mt-3 text-sm text-zinc-400">
            <span className="font-display text-white">{stats.current_cohort.name}</span> • {stats.current_cohort.start_date} → {stats.current_cohort.end_date} • <StatusPill status={stats.current_cohort.status} />
          </div>
        ) : (
          <p className="mt-3 text-sm text-zinc-500">No active cohort. Create one in the Cohorts tab.</p>
        )}
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-lg font-bold">Top 3 right now</h3>
        {stats.top3.length === 0 ? (
          <p className="mt-3 text-sm text-zinc-500">No data yet.</p>
        ) : (
          <ul className="mt-4 space-y-2" data-testid="stats-top3">
            {stats.top3.map((a, i) => (
              <li key={a.id} className="flex items-center justify-between border-b border-zinc-900 py-2">
                <span><span className="font-mono text-zinc-500">#{i + 1}</span> <span className="font-display font-semibold">{a.name}</span> <span className="text-xs text-zinc-500">• {a.college}</span></span>
                <span className="font-mono font-bold text-[#A855F7]">{a.points}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AdminActivityFeed />
    </div>
  );
}

const DEFAULT_REWARD_OPTIONS = [
  "Cash prize",
  "NoobSync swag / merchandise",
  "Internship / job opportunity",
  "Mentorship / 1:1 sessions with founders",
  "Premium NoobSync subscription / credits",
  "Free event tickets / hackathon entries",
  "Featured shoutout on NoobSync channels",
  "Other",
];

function CohortsTab() {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: "Cohort 1",
    start_date: "",
    end_date: "",
    max_ambassadors: 25,
    status: "draft",
    reward_options: DEFAULT_REWARD_OPTIONS,
    theme_name: "",
    sponsors: [],
  });
  const [editing, setEditing] = useState(null);
  const [extending, setExtending] = useState(null);
  const [extendDate, setExtendDate] = useState("");
  const [extendReason, setExtendReason] = useState("");

  const load = () => api.get("/admin/cohorts").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/cohorts", form);
      toast.success("Cohort created");
      setShowCreate(false);
      setForm({
        name: "Cohort 1",
        start_date: "",
        end_date: "",
        max_ambassadors: 25,
        status: "draft",
        reward_options: DEFAULT_REWARD_OPTIONS,
        theme_name: "",
        sponsors: [],
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const update = async () => {
    try {
      const payload = {
        name: editing.name,
        start_date: editing.start_date,
        end_date: editing.end_date,
        max_ambassadors: Number(editing.max_ambassadors),
        status: editing.status,
        reward_options: editing.reward_options || [],
        theme_name: editing.theme_name || null,
        sponsors: editing.sponsors || [],
        prizes: (editing.prizes && editing.prizes.length ? editing.prizes : [
          { rank: 1, amount: 15000, label: "1st place" },
          { rank: 2, amount: 10000, label: "2nd place" },
          { rank: 3, amount: 5000, label: "3rd place" },
        ]).map((p) => ({ ...p, amount: Number(p.amount) || 0 })),
        prize_currency: editing.prize_currency || "₹",
        collect_reward_ideas: !!editing.collect_reward_ideas,
      };
      await api.patch(`/admin/cohorts/${editing.id}`, payload);
      toast.success("Cohort updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const extend = async () => {
    if (!extendDate) { toast.error("Pick a new end date"); return; }
    try {
      await api.post(`/admin/cohorts/${extending.id}/extend`, {
        new_end_date: extendDate,
        reason: extendReason || null,
      });
      toast.success("Cohort extended");
      setExtending(null);
      setExtendDate("");
      setExtendReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extend failed");
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Cohorts</h2>
        <button data-testid="cohort-create-btn" onClick={() => setShowCreate(true)} className="rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white">+ New cohort</button>
      </div>

      <CommunityRewardSignals />

      <div className="space-y-3" data-testid="cohort-list">
        {items.map((c) => (
          <div key={c.id} className="rounded-xl border border-zinc-900 bg-zinc-950 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-lg font-bold">{c.name}</span>
                  <StatusPill status={c.status} />
                  {c.theme_name && (
                    <span data-testid={`cohort-theme-${c.id}`} className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                      🎨 {c.theme_name}
                    </span>
                  )}
                  {c.was_extended && (
                    <span data-testid={`cohort-extended-${c.id}`} className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                      ⏱ Extended on community request
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-zinc-500">{c.start_date} → {c.end_date} • Max {c.max_ambassadors} ambassadors</p>
                {(c.sponsors || []).length > 0 && (
                  <p className="mt-1 text-xs text-zinc-400">
                    <span className="text-zinc-500">Sponsored by</span>{" "}
                    {(c.sponsors || []).map((s, i) => (
                      <span key={`${s.name || "sponsor"}-${i}`} className="text-zinc-200">
                        {s.name}{i < c.sponsors.length - 1 ? <span className="text-zinc-600"> · </span> : null}
                      </span>
                    ))}
                  </p>
                )}
                <p className="mt-1 text-[10px] text-zinc-600">✏️ Editable anytime — duration, capacity, theme, sponsors, reward options, status</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  data-testid={`cohort-extend-${c.id}`}
                  onClick={() => { setExtending(c); setExtendDate(c.end_date || ""); }}
                  className="inline-flex items-center gap-1 rounded border border-amber-400/30 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:border-amber-400/60"
                >
                  ⏱ Extend
                </button>
                <button data-testid={`cohort-edit-${c.id}`} onClick={() => setEditing({ ...c, reward_options: c.reward_options || [], sponsors: c.sponsors || [] })} className="inline-flex items-center gap-1 rounded border border-[#A855F7]/30 bg-[#A855F7]/10 px-3 py-1.5 text-xs font-semibold text-[#E9D5FF] hover:border-[#A855F7]/60">✏️ Edit</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-zinc-500">No cohorts yet.</p>}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="border-zinc-800 bg-zinc-950 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create cohort</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="cohort-form-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="cohort-form-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
              <input data-testid="cohort-form-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
            </div>
            <input data-testid="cohort-form-max" type="number" min={1} placeholder="Max ambassadors" value={form.max_ambassadors} onChange={(e) => setForm({ ...form, max_ambassadors: Number(e.target.value) })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="border-zinc-800 bg-zinc-900"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
            </Select>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-zinc-500">
                Theme name (optional) — e.g. &quot;Diwali edition&quot;, &quot;Founder Cohort&quot;
              </label>
              <input
                data-testid="cohort-form-theme"
                placeholder="Leave empty for a generic cohort"
                value={form.theme_name}
                onChange={(e) => setForm({ ...form, theme_name: e.target.value })}
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-zinc-500">
                Sponsors (optional) — leave empty for generic, add 1+ brands for branded cohorts
              </label>
              <SponsorsEditor value={form.sponsors} onChange={(v) => setForm({ ...form, sponsors: v })} />
            </div>
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-500">
                  Your curated reward pool <span className="text-emerald-400">· internal only</span>
                </label>
                <p className="text-xs text-zinc-400">
                  Your <span className="font-bold text-zinc-200">safe shortlist</span> of rewards you can actually deliver this cohort. <span className="font-bold text-zinc-200">Not shown to applicants</span> — use this as your fallback when community suggestions are unrealistic (e.g. everyone asking for ₹1 crore). Pick winners from this list at announcement time.
                </p>
              </div>
              <RewardOptionsEditor
                value={form.reward_options}
                onChange={(v) => setForm({ ...form, reward_options: v })}
              />
              <p className="text-[11px] text-zinc-500">
                💡 Tip: Cross-reference with the <span className="font-bold text-[#A855F7]">Community reward signals</span> card above to see what people are actually asking for before locking your list.
              </p>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cohort-form-create" onClick={create} className="rounded-md bg-[#A855F7] px-5 py-2 text-sm font-bold text-white">Create</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="border-zinc-800 bg-zinc-950 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit cohort</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
                <input type="date" value={editing.end_date} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
              </div>
              <input type="number" min={1} value={editing.max_ambassadors} onChange={(e) => setEditing({ ...editing, max_ambassadors: Number(e.target.value) })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
              <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                <SelectTrigger className="border-zinc-800 bg-zinc-900"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
              </Select>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-zinc-500">
                  Theme name (optional)
                </label>
                <input
                  data-testid="cohort-edit-theme"
                  placeholder="Leave empty for a generic cohort"
                  value={editing.theme_name || ""}
                  onChange={(e) => setEditing({ ...editing, theme_name: e.target.value })}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-zinc-500">
                  Sponsors (optional)
                </label>
                <SponsorsEditor value={editing.sponsors || []} onChange={(v) => setEditing({ ...editing, sponsors: v })} />
              </div>
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-500">
                    Your curated reward pool <span className="text-emerald-400">· internal only</span>
                  </label>
                  <p className="text-xs text-zinc-400">
                    Your <span className="font-bold text-zinc-200">safe shortlist</span> of rewards you can actually deliver this cohort. <span className="font-bold text-zinc-200">Not shown to applicants</span> — use this as your fallback when community suggestions are unrealistic. Pick winners from this list at announcement time.
                  </p>
                </div>
                <RewardOptionsEditor
                  value={editing.reward_options || []}
                  onChange={(v) => setEditing({ ...editing, reward_options: v })}
                />
              </div>

              <div className="rounded-lg border border-[#A855F7]/30 bg-[#A855F7]/5 p-3 space-y-3" data-testid="cohort-prizes-editor">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#A855F7]">
                    Cash prize pool <span className="text-zinc-400">· shown publicly</span>
                  </label>
                  <p className="text-xs text-zinc-400">Advertised on the landing page + ambassador dashboard. Top 3 at cohort end win these.</p>
                </div>
                {(() => {
                  const prizes = (editing.prizes && editing.prizes.length ? editing.prizes : [
                    { rank: 1, amount: 15000, label: "1st place" },
                    { rank: 2, amount: 10000, label: "2nd place" },
                    { rank: 3, amount: 5000, label: "3rd place" },
                  ]);
                  const setAmt = (idx, amount) => {
                    const next = [0, 1, 2].map((i) => ({ ...prizes[i] }));
                    next[idx] = { ...next[idx], amount: Number(amount) || 0 };
                    setEditing({ ...editing, prizes: next });
                  };
                  return (
                    <div className="flex flex-wrap items-end gap-3">
                      <label className="text-xs text-zinc-400">
                        Currency
                        <input value={editing.prize_currency ?? "₹"} onChange={(e) => setEditing({ ...editing, prize_currency: e.target.value })} data-testid="cohort-prize-currency" className="mt-1 block w-16 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-2 text-sm" />
                      </label>
                      {["🥇 1st", "🥈 2nd", "🥉 3rd"].map((lbl, i) => (
                        <label key={i} className="text-xs text-zinc-400">
                          {lbl}
                          <input type="number" min={0} value={prizes[i].amount} onChange={(e) => setAmt(i, e.target.value)} data-testid={`cohort-prize-${i + 1}`} className="mt-1 block w-28 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
                        </label>
                      ))}
                    </div>
                  );
                })()}
                <label className="flex cursor-pointer items-center gap-2 pt-1">
                  <input type="checkbox" data-testid="cohort-collect-rewards" checked={!!editing.collect_reward_ideas} onChange={(e) => setEditing({ ...editing, collect_reward_ideas: e.target.checked })} className="h-4 w-4 accent-[#A855F7]" />
                  <span className="text-xs text-zinc-300">Collect reward-idea suggestions from applicants (off = show fixed prizes only)</span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={update} className="rounded-md bg-[#A855F7] px-5 py-2 text-sm font-bold text-white">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!extending} onOpenChange={(o) => !o && setExtending(null)}>
        <DialogContent className="border-zinc-800 bg-zinc-950">
          <DialogHeader><DialogTitle>Extend cohort</DialogTitle></DialogHeader>
          {extending && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">
                Push <span className="font-bold text-white">{extending.name}</span> further out.
                Current end date: <span className="font-mono text-zinc-300">{extending.end_date}</span>
              </p>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-500">New end date</label>
                <input
                  data-testid="cohort-extend-date"
                  type="date"
                  min={extending.end_date}
                  value={extendDate}
                  onChange={(e) => setExtendDate(e.target.value)}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-500">Reason (optional)</label>
                <input
                  data-testid="cohort-extend-reason"
                  placeholder="e.g. Community request, exam season, partner delay"
                  value={extendReason}
                  onChange={(e) => setExtendReason(e.target.value)}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
                />
              </div>
              <p className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
                A <strong>&quot;⏱ Extended on community request&quot;</strong> chip will appear on the landing hero and dashboard so ambassadors know the change isn&apos;t arbitrary.
              </p>
            </div>
          )}
          <DialogFooter>
            <button data-testid="cohort-extend-confirm" onClick={extend} className="rounded-md bg-amber-500 px-5 py-2 text-sm font-bold text-zinc-950 hover:bg-amber-400">
              Confirm extension
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SponsorsEditor({ value, onChange }) {
  const list = value || [];
  const set = (i, key, val) => {
    const next = [...list];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const add = () =>
    onChange([
      ...list,
      { _uid: `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: "", logo_url: "", website_url: "" },
    ]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2" data-testid="sponsors-editor">
      {list.map((s, i) => (
        <div key={s._uid || `${s.name || "row"}-${i}`} className="rounded-md border border-zinc-800 bg-zinc-900/50 p-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input
              placeholder="Brand name *"
              value={s.name || ""}
              onChange={(e) => set(i, "name", e.target.value)}
              data-testid={`sponsor-name-${i}`}
              className="rounded border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-sm"
            />
            <input
              placeholder="Website URL (optional)"
              value={s.website_url || ""}
              onChange={(e) => set(i, "website_url", e.target.value)}
              data-testid={`sponsor-url-${i}`}
              className="rounded border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`sponsor-remove-${i}`}
              className="rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-xs font-bold text-rose-200 hover:border-rose-500/60"
            >
              Remove
            </button>
          </div>
          <input
            placeholder="Logo URL (optional, e.g. CDN https link)"
            value={s.logo_url || ""}
            onChange={(e) => set(i, "logo_url", e.target.value)}
            data-testid={`sponsor-logo-${i}`}
            className="mt-2 w-full rounded border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-sm"
          />
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid="sponsor-add"
        className="rounded border border-zinc-700 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-zinc-500"
      >
        + Add sponsor
      </button>
    </div>
  );
}


/**
 * CommunityRewardSignals
 * Aggregator of every reward suggestion across all applications + pre-cohort
 * interest signals. Lives on the Cohorts tab so admins reference real demand
 * data while planning the next cohort.
 */
function CommunityRewardSignals() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    api.get("/admin/interest-signals").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { aggregated = [], signals_count = 0, applications_count = 0 } = data;
  if (!aggregated.length) {
    return (
      <div className="mb-5 rounded-2xl border border-zinc-800 bg-zinc-950 p-4 text-sm text-zinc-500" data-testid="community-rewards-empty">
        🎁 Community reward suggestions will appear here as applicants &amp; interest signals come in.
      </div>
    );
  }
  const top = aggregated[0];
  const maxCount = top?.count || 1;

  return (
    <div className="mb-5 rounded-2xl border border-[#A855F7]/30 bg-gradient-to-br from-[#A855F7]/[0.06] to-zinc-950 p-4 sm:p-5" data-testid="community-rewards">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🎁</span>
          <div>
            <h3 className="font-display text-base font-bold text-white">Community reward signals</h3>
            <p className="text-xs text-zinc-500">
              {aggregated.length} unique suggestions · from {applications_count} application{applications_count === 1 ? "" : "s"} + {signals_count} pre-cohort signal{signals_count === 1 ? "" : "s"} · top pick: <span className="font-bold text-[#A855F7]">{top.display}</span> (×{top.count})
            </p>
          </div>
        </div>
        <span className="text-xs text-zinc-500">{open ? "▾ collapse" : "▸ expand"}</span>
      </button>

      {open && (
        <ul className="mt-4 space-y-1.5" data-testid="community-rewards-list">
          {aggregated.slice(0, 25).map((b, i) => {
            const pct = (b.count / maxCount) * 100;
            const fromApps = b.sources?.application || 0;
            const fromSignals = b.sources?.interest || 0;
            return (
              <li key={b.display + i} className="group relative overflow-hidden rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2">
                <div
                  className="absolute inset-y-0 left-0 bg-[#A855F7]/15 transition-all"
                  style={{ width: `${pct}%` }}
                />
                <div className="relative flex flex-wrap items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-[10px] text-zinc-500">#{i + 1}</span>
                    <span className="font-medium text-zinc-100">{b.display}</span>
                  </span>
                  <span className="flex items-center gap-2 text-[11px]">
                    <span className="rounded bg-[#A855F7]/20 px-1.5 py-0.5 font-bold text-[#A855F7]">×{b.count}</span>
                    <span className="hidden text-zinc-500 sm:inline">
                      {b.unique_people} {b.unique_people === 1 ? "person" : "people"}
                    </span>
                    {fromApps > 0 && fromSignals > 0 && (
                      <span className="hidden text-zinc-600 sm:inline">· {fromApps} app + {fromSignals} signal</span>
                    )}
                  </span>
                </div>
              </li>
            );
          })}
          {aggregated.length > 25 && (
            <li className="text-center text-xs text-zinc-500">+ {aggregated.length - 25} more suggestions</li>
          )}
        </ul>
      )}
    </div>
  );
}


/**
 * RewardSuggestionsAggregator
 * Renders a collapsible card on the Apps tab summarizing every `reward_ideas`
 * value across the current Applications list. Splits on " | ", counts unique
 * suggestions case-insensitively, sorts by frequency. Helps admins pick the
 * actual reward pool based on community demand.
 */
function RewardSuggestionsAggregator({ items }) {
  const [open, setOpen] = useState(true);

  const buckets = useMemo(() => {
    const counts = new Map(); // key=lowercased, value={count, display, applicants:Set}
    (items || []).forEach((app) => {
      if (!app.reward_ideas) return;
      const parts = String(app.reward_ideas)
        .split(/\s*\|\s*/)
        .map((s) => s.trim())
        .filter(Boolean);
      parts.forEach((raw) => {
        const k = raw.toLowerCase();
        const cur = counts.get(k) || { count: 0, display: raw, applicants: new Set() };
        cur.count += 1;
        cur.applicants.add(app.full_name || app.email || app.id);
        counts.set(k, cur);
      });
    });
    return Array.from(counts.values()).sort((a, b) => b.count - a.count);
  }, [items]);

  const totalApps = (items || []).filter((a) => a.reward_ideas).length;
  if (!totalApps) return null;
  const top = buckets[0];
  const maxCount = top?.count || 1;

  return (
    <div className="mb-5 rounded-2xl border border-[#A855F7]/30 bg-gradient-to-br from-[#A855F7]/[0.06] to-zinc-950 p-4 sm:p-5" data-testid="reward-aggregator">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-2 text-left"
        data-testid="reward-agg-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🎁</span>
          <div>
            <h3 className="font-display text-base font-bold text-white">
              Reward suggestions — what the community is asking for
            </h3>
            <p className="text-xs text-zinc-500">
              {buckets.length} unique idea{buckets.length === 1 ? "" : "s"} from {totalApps} applicant{totalApps === 1 ? "" : "s"} · top pick: <span className="font-bold text-[#A855F7]">{top?.display}</span> ({top?.count}×)
            </p>
          </div>
        </div>
        <span className="text-xs text-zinc-500">{open ? "▾ collapse" : "▸ expand"}</span>
      </button>

      {open && (
        <ul className="mt-4 space-y-1.5" data-testid="reward-agg-list">
          {buckets.slice(0, 25).map((b, i) => {
            const pct = (b.count / maxCount) * 100;
            return (
              <li key={b.display + i} className="group relative overflow-hidden rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2">
                <div
                  className="absolute inset-y-0 left-0 bg-[#A855F7]/15 transition-all"
                  style={{ width: `${pct}%` }}
                />
                <div className="relative flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-[10px] text-zinc-500">#{i + 1}</span>
                    <span className="font-medium text-zinc-100">{b.display}</span>
                  </span>
                  <span className="flex items-center gap-2 text-xs">
                    <span className="rounded bg-[#A855F7]/20 px-1.5 py-0.5 font-bold text-[#A855F7]">×{b.count}</span>
                    <span className="hidden text-zinc-500 sm:inline" title={Array.from(b.applicants).join(", ")}>
                      from {b.applicants.size} applicant{b.applicants.size === 1 ? "" : "s"}
                    </span>
                  </span>
                </div>
              </li>
            );
          })}
          {buckets.length > 25 && (
            <li className="text-center text-xs text-zinc-500">+ {buckets.length - 25} more suggestions</li>
          )}
        </ul>
      )}
    </div>
  );
}

function ApplicationsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ status: "", q: "" });
  const [selected, setSelected] = useState({});
  const [open, setOpen] = useState(null);

  const load = () => {
    const p = new URLSearchParams();
    if (filter.status) p.set("status", filter.status);
    if (filter.q) p.set("q", filter.q);
    api.get(`/admin/applications?${p}`).then((r) => setItems(r.data.items));
  };
  useEffect(() => { load(); }, [filter.status]);

  const decide = async (id, decision, note = null) => {
    try {
      await api.post(`/admin/applications/${id}/decision`, { decision, note });
      toast.success(`Application ${decision}ed`);
      setOpen(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const bulk = async (decision) => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) { toast.error("Select applications first"); return; }
    try {
      await api.post("/admin/applications/bulk-decision", { application_ids: ids, decision });
      toast.success("Bulk action complete");
      setSelected({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl font-bold flex-1">Applications</h2>
        <Select value={filter.status || "all"} onValueChange={(v) => setFilter({ ...filter, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 border-zinc-800 bg-zinc-900" data-testid="apps-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <input data-testid="apps-filter-q" placeholder="Search name/email/college" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} onBlur={load} className="w-72 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
        <a href={`${API}/admin/applications/export.csv`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-3 py-2 text-xs hover:border-zinc-700" data-testid="apps-export"><Download className="h-3 w-3" /> CSV</a>
        <button data-testid="apps-bulk-accept" onClick={() => bulk("accept")} className="rounded-md bg-[#A855F7] px-3 py-2 text-xs font-bold text-white">Bulk accept</button>
        <button data-testid="apps-bulk-reject" onClick={() => bulk("reject")} className="rounded-md border border-rose-500/50 px-3 py-2 text-xs font-bold text-rose-300 hover:bg-rose-500/10">Bulk reject</button>
      </div>

      <RewardSuggestionsAggregator items={items} />

      <div className="overflow-x-auto rounded-xl border border-zinc-900 bg-zinc-950">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-900 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="p-3"><input type="checkbox" onChange={(e) => {
                const sel = {}; if (e.target.checked) items.forEach((i) => (sel[i.id] = true)); setSelected(sel);
              }} /></th>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">College</th>
              <th className="p-3 text-left">IG</th>
              <th className="p-3 text-left">LI</th>
              <th className="p-3 text-left">Applied</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody data-testid="apps-table-body">
            {items.map((a) => (
              <tr key={a.id} className="border-b border-zinc-900">
                <td className="p-3"><input type="checkbox" checked={!!selected[a.id]} onChange={(e) => setSelected({ ...selected, [a.id]: e.target.checked })} /></td>
                <td className="p-3">
                  <div className="font-display font-semibold flex items-center gap-2">
                    {a.full_name}
                    {a.duplicate_flags && a.duplicate_flags.length > 0 && (
                      <span data-testid={`dup-badge-${a.id}`} title={a.duplicate_flags.map((f) => `${f.field}: ${f.matches.length} match`).join(", ")} className="rounded border border-amber-500/50 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-300">
                        ⚠ DUP {a.duplicate_flags.map((f) => f.field[0].toUpperCase()).join("")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500">{a.email}</div>
                  {a.referrer_code && (
                    <div data-testid={`apps-ref-${a.id}`} title={`Referred by ${a.referrer_name || a.referrer_code} (${a.referrer_code})`} className="mt-0.5 text-[11px] font-semibold text-[#A855F7]">
                      ↳ Referred by {a.referrer_name || a.referrer_code}
                    </div>
                  )}
                </td>
                <td className="p-3 text-zinc-300">{a.college || <span className="text-zinc-600">—</span>}<div className="text-xs text-zinc-500">{a.city_state}</div></td>
                <td className="p-3 font-mono">{a.instagram_followers}</td>
                <td className="p-3 font-mono">{a.linkedin_followers}</td>
                <td className="p-3 text-xs text-zinc-500">{new Date(a.created_at).toLocaleDateString()}</td>
                <td className="p-3"><StatusPill status={a.status} /></td>
                <td className="p-3"><button onClick={() => { setOpen(a); api.get(`/admin/applications/${a.id}`).then((r) => setOpen(r.data)).catch(() => {}); }} className="rounded border border-zinc-800 px-3 py-1 text-xs hover:border-zinc-600" data-testid={`apps-view-${a.id}`}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="p-6 text-center text-zinc-500">No applications.</p>}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto border-zinc-800 bg-zinc-950 sm:max-w-2xl">
          {open && (
            <>
              <DialogHeader><DialogTitle>{open.full_name}</DialogTitle></DialogHeader>
              {open.duplicate_flags && open.duplicate_flags.length > 0 && (
                <div data-testid={`dup-banner-${open.id}`} className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
                  <p className="font-bold uppercase tracking-wider text-amber-300">⚠ Duplicate flags</p>
                  <ul className="mt-1 space-y-1 text-amber-200">
                    {open.duplicate_flags.map((f) => (
                      <li key={f.field}><b className="capitalize">{f.field}:</b> {f.matches.length} other application{f.matches.length > 1 ? "s" : ""} use the same value — {f.matches.map((m) => `${m.name} (${m.status})`).join(", ")}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="space-y-2 text-sm text-zinc-300">
                <p className="text-zinc-500">{open.email} • {open.city_state}</p>
                {open.referrer_code && (
                  <p data-testid="app-referrer">
                    <b className="text-zinc-500">Referred by:</b>{" "}
                    {open.referrer_name && <span className="font-semibold text-zinc-200">{open.referrer_name}</span>}{" "}
                    <span className="rounded border border-[#A855F7]/40 bg-[#A855F7]/10 px-1.5 py-0.5 font-mono text-xs text-[#A855F7]">{open.referrer_code}</span>
                    <span className="ml-2 text-xs text-zinc-500">+50 / +0 will auto-credit on accept</span>
                  </p>
                )}
                {open.self_description && <p><b className="text-zinc-500">Profile:</b> {open.self_description}</p>}
                {open.college && <p><b className="text-zinc-500">College:</b> {open.college}</p>}
                {(open.year_of_study || open.course) && <p><b className="text-zinc-500">Year/Course:</b> {open.year_of_study || "—"} • {open.course || "—"}</p>}
                {open.graduation_year && <p><b className="text-zinc-500">Graduated:</b> {open.graduation_year}</p>}
                {open.company_name && <p><b className="text-zinc-500">Company:</b> {open.company_name} {open.role_title && <span>— {open.role_title}</span>}</p>}
                {open.industry && <p><b className="text-zinc-500">Industry:</b> {open.industry}</p>}
                {open.startup_stage && <p><b className="text-zinc-500">Startup stage:</b> {open.startup_stage}</p>}
                {open.content_niche && <p><b className="text-zinc-500">Niche:</b> {open.content_niche}{open.primary_platform ? ` • ${open.primary_platform}` : ""}</p>}
                {open.brand_name && <p><b className="text-zinc-500">Brand:</b> {open.brand_name}{open.brand_website ? ` — ${open.brand_website}` : ""}</p>}
                {open.brand_audience_size && <p><b className="text-zinc-500">Audience size:</b> {open.brand_audience_size}</p>}
                {open.custom_description && <p><b className="text-zinc-500">About:</b> {open.custom_description}</p>}
                <p><b className="text-zinc-500">Instagram:</b> {open.instagram_handle} ({open.instagram_followers})</p>
                <p><b className="text-zinc-500">LinkedIn:</b> <a href={open.linkedin_url} className="text-[#A855F7]" target="_blank" rel="noreferrer">{open.linkedin_url}</a> ({open.linkedin_followers})</p>
                {open.youtube_url && <p><b className="text-zinc-500">YouTube:</b> {open.youtube_url} ({open.youtube_subscribers})</p>}
                {open.twitter_handle && <p><b className="text-zinc-500">Twitter / X:</b> {open.twitter_handle} ({open.twitter_followers})</p>}
                {open.hackathons_experience && <div><b className="text-zinc-500">Hackathons:</b><p className="mt-1 rounded bg-zinc-900 p-2">{open.hackathons_experience}</p></div>}
                <div className="mt-3 rounded-lg border border-[#22D3EE]/25 bg-[#22D3EE]/[0.04] p-3" data-testid="app-follow-status">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#22D3EE]">
                    Follows NoobSync socials: {open.consent_follow_socials ? "✅ Confirmed" : "❌ Not confirmed"}
                  </p>
                  <p className="mt-1 text-xs text-zinc-500">Applicant confirmed following at signup — verify before approving.</p>
                </div>
                <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3" data-testid="app-answers">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]">Story &amp; reward suggestions</p>
                  <div className="mt-3 space-y-3">
                    {[
                      ["Why they want to join (motivation)", open.motivation],
                      ["What they expect from the program", open.expectations],
                      ["How they'll promote NoobSync", open.promotion_plan],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <b className="text-zinc-500">{label}:</b>
                        {val
                          ? <p className="mt-1 whitespace-pre-wrap rounded bg-zinc-900 p-2 text-zinc-200">{val}</p>
                          : <p className="mt-1 italic text-zinc-600">Not provided</p>}
                      </div>
                    ))}
                    <div data-testid="app-reward-suggestions">
                      <b className="text-zinc-500">Reward suggestions (up to 3):</b>
                      {open.reward_ideas
                        ? (
                          <ul className="mt-1 space-y-1">
                            {open.reward_ideas.split(/\s*\|\s*/).filter(Boolean).map((idea, i) => (
                              <li key={i} className="rounded bg-zinc-900 p-2 text-sm">
                                <span className="mr-2 inline-block rounded bg-[#A855F7]/20 px-1.5 py-0.5 text-[10px] font-bold text-[#A855F7]">#{i + 1}</span>
                                {idea}
                              </li>
                            ))}
                          </ul>
                        )
                        : <p className="mt-1 italic text-zinc-600">No reward ideas suggested</p>}
                    </div>
                  </div>
                </div>
                {open.brand_logo_path && (
                  <div>
                    <b className="text-zinc-500">Brand logo:</b>
                    <div className="mt-2 inline-block rounded-md border border-zinc-800 bg-white p-2">
                      <img src={`${API}/files/${open.brand_logo_path}`} alt="brand logo" className="max-h-24" />
                    </div>
                  </div>
                )}
              </div>
              <VerifySocialPanel application={open} onUpdated={() => {
                api.get(`/admin/applications/${open.id}`).then((r) => setOpen(r.data));
              }} />
              <DialogFooter className="gap-2">
                {open.status === "pending" && (
                  <>
                    <button onClick={() => decide(open.id, "reject")} className="rounded-md border border-rose-500/50 px-4 py-2 text-sm text-rose-300 hover:bg-rose-500/10" data-testid={`apps-reject-${open.id}`}>Reject</button>
                    <button onClick={() => decide(open.id, "accept")} className="rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white" data-testid={`apps-accept-${open.id}`}>Accept</button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AmbassadorsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/ambassadors").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const view = async (id) => {
    const r = await api.get(`/admin/ambassadors/${id}`);
    setOpen(r.data);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this ambassador?")) return;
    await api.delete(`/admin/ambassadors/${id}`);
    toast.success("Removed");
    setOpen(null);
    load();
  };

  const toggleCM = async (id, enabled) => {
    await api.patch(`/admin/ambassadors/${id}/content-manager`, null, { params: { enabled } });
    toast.success(enabled ? "Granted Content Manager access" : "Revoked Content Manager access");
    if (open) view(id);
    load();
  };

  const toggleCampus = async (id, enabled) => {
    await api.patch(`/admin/ambassadors/${id}/campus-ambassador`, null, { params: { enabled } });
    toast.success(enabled ? "Marked as Campus Ambassador (off-leaderboard)" : "Removed Campus Ambassador role");
    if (open) view(id);
    load();
  };

  const awardFullWeek = async (id) => {
    if (!window.confirm("Award this ambassador the FULL weekly max points for the current config (as if they completed every activity that week)? This is a testing tool — use it on test accounts.")) return;
    try {
      const r = await api.post(`/admin/ambassadors/${id}/award-full-week`);
      const d = r.data;
      toast.success(`+${d.awarded_total} pts awarded (week ${d.weeks_awarded}) · new total ${d.new_total_points}`);
      if (d.dynamic_caps_enabled) toast.info("Note: dynamic caps are ON — this used standard weekly caps for the ceiling.");
      view(id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not award points");
    }
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold mb-5">Ambassadors</h2>
      <div className="overflow-x-auto rounded-xl border border-zinc-900 bg-zinc-950">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-900 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">College</th><th className="p-3 text-left">Code</th><th className="p-3 text-left">Points</th><th className="p-3 text-left">Subs</th><th className="p-3 text-left">Refs</th><th className="p-3"></th></tr>
          </thead>
          <tbody data-testid="amb-table">
            {items.map((a) => (
              <tr key={a.id} className="border-b border-zinc-900">
                <td className="p-3"><div className="font-semibold">{a.full_name}{a.is_content_manager && <span className="ml-2 rounded bg-[#A855F7]/15 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-[#A855F7]" data-testid={`amb-cm-badge-${a.id}`}>Content Mgr</span>}{a.is_campus_ambassador && <span className="ml-2 rounded bg-cyan-500/15 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-cyan-300" data-testid={`amb-campus-badge-${a.id}`}>Campus Amb</span>}</div><div className="text-xs text-zinc-500">{a.email}</div></td>
                <td className="p-3 text-zinc-300">{a.college}</td>
                <td className="p-3 font-mono text-[#A855F7]">{a.referral_code}</td>
                <td className="p-3 font-mono font-bold text-[#A855F7]">{a.total_points}</td>
                <td className="p-3 font-mono">{a.submission_count}</td>
                <td className="p-3 font-mono">{a.referral_count}</td>
                <td className="p-3"><button onClick={() => view(a.id)} className="rounded border border-zinc-800 px-3 py-1 text-xs" data-testid={`amb-view-${a.id}`}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="p-6 text-center text-zinc-500">No ambassadors yet.</p>}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto border-zinc-800 bg-zinc-950 sm:max-w-2xl">
          {open && (
            <>
              <DialogHeader><DialogTitle>{open.ambassador.full_name}</DialogTitle></DialogHeader>
              <div className="text-sm text-zinc-400">
                <p>{open.ambassador.email} • {open.ambassador.college}</p>
                <p className="mt-1 font-mono text-[#A855F7]">{open.ambassador.referral_code}</p>
                {open.ambassador.referral_link && (
                  <div className="mt-2 flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 p-2" data-testid="amb-referral-link-row">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">Referral link</span>
                    <a href={open.ambassador.referral_link} target="_blank" rel="noreferrer" data-testid="amb-referral-link" className="flex-1 truncate font-mono text-xs text-cyan-300 hover:underline">{open.ambassador.referral_link}</a>
                    <button
                      onClick={() => { navigator.clipboard.writeText(open.ambassador.referral_link); toast.success("Referral link copied"); }}
                      data-testid="amb-copy-referral-link"
                      className="shrink-0 rounded border border-zinc-700 px-2 py-1 text-[11px] font-semibold text-zinc-200 hover:border-[#A855F7]"
                    >Copy</button>
                  </div>
                )}
                <p className="mt-2"><b>{open.ambassador.total_points}</b> points • {open.submissions.length} submissions • {open.referrals.length} referrals</p>
              </div>
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Recent submissions</p>
                <ul className="mt-2 space-y-1 text-sm">
                  {open.submissions.slice(0, 10).map((s) => (
                    <li key={s.id} className="flex items-center justify-between border-b border-zinc-900 py-1">
                      <span className="capitalize">{s.content_type.replace(/_/g, " ")}</span>
                      <span><StatusPill status={s.status} /></span>
                    </li>
                  ))}
                </ul>
              </div>
              <AmbassadorSocialsEditor ambassador={open.ambassador} onUpdated={() => view(open.ambassador.id)} />
              <div className="mt-4 rounded-lg border border-emerald-900/60 bg-emerald-950/20 p-4" data-testid="award-full-week-card">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-400">Testing · simulate a full week</p>
                <p className="mt-1 text-xs text-zinc-400">
                  Marks this ambassador as having completed <b>every activity</b> for one week and awards the max weekly content points for the current config (weekly caps × points). Handy for a test account to see the ceiling a config produces. Repeat to simulate more weeks. Referrals &amp; streak bonus not included.
                  {open.ambassador.admin_test_weeks_awarded > 0 && (
                    <span className="mt-1 block text-emerald-300">Full weeks awarded so far: {open.ambassador.admin_test_weeks_awarded}</span>
                  )}
                </p>
                <button onClick={() => awardFullWeek(open.ambassador.id)} data-testid="amb-award-full-week" className="mt-3 rounded-md bg-emerald-500 px-4 py-2 text-sm font-bold text-black hover:bg-emerald-400">
                  Mark full week &amp; award max points
                </button>
              </div>
              <PointsAdminControl ambassador={open.ambassador} onDone={() => { view(open.ambassador.id); load(); }} />
              <AmbassadorPerkControl ambassador={open.ambassador} onDone={() => view(open.ambassador.id)} />
              <div className="mt-4 rounded-lg border border-zinc-900 bg-zinc-900/40 p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Content Manager access</p>
                <p className="mt-1 text-xs text-zinc-400">
                  {open.ambassador.is_content_manager
                    ? "This person can upload resources (pending your approval) and view the library read-only."
                    : "Grant this person upload + read-only access to the Content Library. They can't delete or publish — their uploads come to you for approval."}
                </p>
                {open.ambassador.is_content_manager ? (
                  <button onClick={() => toggleCM(open.ambassador.id, false)} data-testid="amb-revoke-cm" className="mt-3 rounded-md border border-amber-500/50 px-4 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-500/10">Revoke Content Manager</button>
                ) : (
                  <button onClick={() => toggleCM(open.ambassador.id, true)} data-testid="amb-grant-cm" className="mt-3 rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white">Make Content Manager</button>
                )}
              </div>
              <div className="mt-4 rounded-lg border border-cyan-900/60 bg-cyan-950/20 p-4" data-testid="amb-campus-card">
                <p className="text-xs font-bold uppercase tracking-wider text-cyan-300">Campus Ambassador role</p>
                <p className="mt-1 text-xs text-zinc-400">
                  {open.ambassador.is_campus_ambassador
                    ? "This person is a referral-focused Campus Ambassador — hidden from all leaderboards (public + dashboard) and excluded from everyone else's rank. Their referral points won't overshadow content creators."
                    : "Make this person a referral-focused Campus Ambassador. They'll be removed from all leaderboards and won't affect anyone else's rank — ideal for a high-volume referrer who'd otherwise scare off content creators."}
                </p>
                {open.ambassador.is_campus_ambassador ? (
                  <button onClick={() => toggleCampus(open.ambassador.id, false)} data-testid="amb-revoke-campus" className="mt-3 rounded-md border border-amber-500/50 px-4 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-500/10">Remove Campus Ambassador role</button>
                ) : (
                  <button onClick={() => toggleCampus(open.ambassador.id, true)} data-testid="amb-make-campus" className="mt-3 rounded-md bg-cyan-500 px-4 py-2 text-sm font-bold text-black hover:bg-cyan-400">Make Campus Ambassador</button>
                )}
              </div>
              <DialogFooter>
                <button onClick={() => remove(open.ambassador.id)} className="rounded-md border border-rose-500/50 px-4 py-2 text-sm text-rose-300 hover:bg-rose-500/10" data-testid="amb-remove">Remove ambassador</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AmbassadorPerkControl({ ambassador, onDone }) {
  const [products, setProducts] = useState([]);
  const [productKey, setProductKey] = useState("");
  const [perk, setPerk] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/referral-products").then((r) => {
      const list = r.data.products || r.data.items || [];
      setProducts(list);
      setProductKey((prev) => prev || list[0]?.product_key || "ideathon");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setPerk(((ambassador.perks || {})[productKey]) || null);
  }, [productKey, ambassador]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/ambassadors/${ambassador.id}/perk`, { product_key: productKey, perk });
      toast.success(perk ? "Perk override saved" : "Override cleared — using cohort default");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save perk");
    } finally {
      setSaving(false);
    }
  };

  const opts = products.length ? products : [{ product_key: "ideathon", product_name: "IdeaThon" }];
  return (
    <div className="mt-4 rounded-lg border border-[#A855F7]/30 bg-[#A855F7]/[0.04] p-4" data-testid="amb-perk-control">
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#A855F7]"><Gift className="h-3.5 w-3.5" /> Referral perk override</p>
      <p className="mt-1 text-xs text-zinc-400">Give THIS ambassador&apos;s code a special discount for a product — overrides the cohort default. Remove it to fall back to the default.</p>
      <div className="mt-3">
        <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Product</label>
        <select data-testid="amb-perk-product" value={productKey} onChange={(e) => setProductKey(e.target.value)} className="w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white outline-none focus:border-[#A855F7]">
          {opts.map((p) => <option key={p.product_key} value={p.product_key}>{p.product_name || p.product_key}</option>)}
        </select>
      </div>
      <div className="mt-3"><PerkFields perk={perk} onChange={setPerk} idPrefix="amb-perk" /></div>
      <button onClick={save} disabled={saving} data-testid="amb-perk-save" className="mt-3 rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white hover:bg-[#9333EA] disabled:opacity-60">
        {saving ? "Saving…" : "Save perk override"}
      </button>
    </div>
  );
}

function PointsAdminControl({ ambassador, onDone }) {
  const [val, setVal] = useState(String(ambassador.total_points ?? 0));
  const save = async () => {
    const v = parseInt(val, 10);
    if (isNaN(v) || v < 0) return toast.error("Enter a valid non-negative number");
    try {
      const r = await api.patch(`/admin/ambassadors/${ambassador.id}/points`, { total_points: v });
      toast.success(`Points set to ${r.data.total_points}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reset = async () => {
    if (!window.confirm("Reset to genuinely-earned points? This drops any manual override and test 'full week' grants, recomputing from real approved submissions + referrals.")) return;
    try {
      const r = await api.post(`/admin/ambassadors/${ambassador.id}/recompute`);
      toast.success(`Reset to earned points: ${r.data.total_points}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="mt-4 rounded-lg border border-zinc-900 bg-zinc-900/40 p-4" data-testid="set-points-card">
      <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
        Adjust points
        {ambassador.points_manually_set && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">manually set</span>}
      </p>
      <p className="mt-1 text-xs text-zinc-400">Override total points to an exact value (testing), or reset to genuinely-earned points (real approved submissions + referrals + streak).</p>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <input type="number" min="0" data-testid="set-points-input" value={val} onChange={(e) => setVal(e.target.value)} className="w-32 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
        <button onClick={save} data-testid="set-points-save" className="rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white">Set points</button>
        <button onClick={reset} data-testid="reset-points-btn" className="rounded-md border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-500 hover:text-white">Reset to earned points</button>
      </div>
    </div>
  );
}


function ContentTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ status: "pending" });
  const [open, setOpen] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => {
    const p = new URLSearchParams();
    if (filter.status) p.set("status", filter.status);
    api.get(`/admin/content?${p}`).then((r) => setItems(r.data.items));
  };
  useEffect(() => { load(); }, [filter.status]);

  const decide = async (id, decision) => {
    try {
      await api.post(`/admin/content/${id}/review`, { decision, rejection_reason: decision === "reject" ? reason : null });
      toast.success(`Marked ${decision}d`);
      setOpen(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <h2 className="font-display text-2xl font-bold flex-1">Content review</h2>
        <Select value={filter.status} onValueChange={(v) => setFilter({ status: v })}>
          <SelectTrigger className="w-40 border-zinc-800 bg-zinc-900" data-testid="content-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="mb-4 text-xs text-zinc-500">⏱ Aim to review within 24–48 hours.</p>

      <div className="grid gap-3 md:grid-cols-2" data-testid="content-list">
        {items.map((c) => (
          <div key={c.id} className="rounded-xl border border-zinc-900 bg-zinc-950 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display font-bold">{CT_LABEL[c.content_type] || c.content_type}</p>
                <p className="text-xs text-zinc-500">{c.ambassador_name} • {c.ambassador_college || "—"}</p>
              </div>
              <div className="flex items-center gap-1">
                {c.flagged_burst && (
                  <span data-testid={`flag-burst-${c.id}`} title={`${c.burst_24h} submissions in last 24h`} className="rounded border border-amber-500/50 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-300">
                    ⚠ {c.burst_24h}/24h
                  </span>
                )}
                <StatusPill status={c.status} />
              </div>
            </div>
            <p className="mt-3 text-xs text-zinc-500">{new Date(c.submitted_at).toLocaleString()}</p>
            {c.proof_url && <a href={c.proof_url} target="_blank" rel="noreferrer" className="mt-2 block truncate text-xs text-[#A855F7] hover:underline">🔗 {c.proof_url}</a>}
            {c.original_asset_url && <a href={c.original_asset_url} target="_blank" rel="noreferrer" className="mt-1 block truncate text-xs text-[#22D3EE] hover:underline">↻ Original: {c.original_asset_url}</a>}
            {(() => {
              const paths = c.proof_file_paths?.length ? c.proof_file_paths : (c.proof_file_path ? [c.proof_file_path] : []);
              if (paths.length === 0) return null;
              return (
                <div className="mt-2" data-testid={`content-thumbs-${c.id}`}>
                  {paths.length > 1 && <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500">Carousel — {paths.length} slides</p>}
                  <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
                    {paths.map((p, i) => (
                      /\.(jpe?g|png|webp|gif|svg)$/i.test(p) || (c.proof_content_type || "").startsWith("image/") ? (
                        <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="relative block aspect-square overflow-hidden rounded border border-zinc-800 bg-zinc-950">
                          <img src={`${API}/files/${p}`} alt={`proof ${i + 1}`} className="h-full w-full object-cover" />
                          {paths.length > 1 && <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[10px] font-bold text-white">{i + 1}</span>}
                        </a>
                      ) : (
                        <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="flex aspect-square items-center justify-center rounded border border-zinc-800 bg-zinc-950 text-[10px] text-[#A855F7] hover:underline">📎 file {i + 1}</a>
                      )
                    ))}
                  </div>
                </div>
              );
            })()}
            {c.note && <p className="mt-2 rounded bg-zinc-900 p-2 text-xs text-zinc-400">{c.note}</p>}
            {c.status === "pending" && (
              <button onClick={() => setOpen(c)} className="mt-3 rounded border border-zinc-800 px-3 py-1 text-xs hover:border-zinc-600" data-testid={`content-review-${c.id}`}>Review</button>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="col-span-2 text-sm text-zinc-500">Nothing in this queue.</p>}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="border-zinc-800 bg-zinc-950">
          <DialogHeader><DialogTitle>Review submission</DialogTitle></DialogHeader>
          {open && (
            <div className="text-sm text-zinc-300">
              <p><b className="text-zinc-500">Ambassador:</b> {open.ambassador_name}</p>
              <p><b className="text-zinc-500">Type:</b> {CT_LABEL[open.content_type] || open.content_type}</p>
              {open.flagged_burst && <p className="rounded border border-amber-500/40 bg-amber-500/10 p-2 text-amber-300">⚠ This ambassador submitted {open.burst_24h} items in the last 24 hours.</p>}
              {open.proof_url && <p><b className="text-zinc-500">URL:</b> <a href={open.proof_url} target="_blank" rel="noreferrer" className="text-[#A855F7]">{open.proof_url}</a></p>}
              {(() => {
                const paths = open.proof_file_paths?.length ? open.proof_file_paths : (open.proof_file_path ? [open.proof_file_path] : []);
                if (paths.length === 0) return null;
                return (
                  <div className="mt-3">
                    <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500">Attachments {paths.length > 1 ? `— ${paths.length} slides` : ""}</p>
                    <div className="grid max-h-[320px] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
                      {paths.map((p, i) => (
                        /\.(jpe?g|png|webp|gif|svg)$/i.test(p) ? (
                          <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="relative block aspect-square overflow-hidden rounded border border-zinc-800 bg-zinc-950">
                            <img src={`${API}/files/${p}`} alt={`slide ${i + 1}`} className="h-full w-full object-cover" />
                            {paths.length > 1 && <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[10px] font-bold text-white">{i + 1}</span>}
                          </a>
                        ) : (
                          <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="flex aspect-square items-center justify-center rounded border border-zinc-800 bg-zinc-950 text-xs text-[#A855F7] hover:underline">📎 file {i + 1}</a>
                        )
                      ))}
                    </div>
                  </div>
                );
              })()}
              {open.note && <p className="mt-1 text-zinc-400">Note: {open.note}</p>}
              <div className="mt-4">
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-500">Rejection reason (if rejecting)</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" data-testid="content-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => decide(open.id, "reject")} className="rounded-md border border-rose-500/50 px-4 py-2 text-sm text-rose-300 hover:bg-rose-500/10" data-testid="content-reject-btn"><XCircle className="mr-1 inline h-4 w-4" />Reject</button>
            <button onClick={() => decide(open.id, "approve")} className="rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white" data-testid="content-approve-btn"><CheckCircle className="mr-1 inline h-4 w-4" />Approve</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PointsConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [cohortId, setCohortId] = useState(null);
  const [products, setProducts] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/points-config");
    setCfg({ points: r.data.points_config, caps: r.data.weekly_caps, hasCohort: !!r.data.cohort });
    setCohortId(r.data.cohort?.id || null);
    if (r.data.cohort?.id) {
      const c = await api.get("/admin/cohorts");
      const active = c.data.items.find((x) => x.id === r.data.cohort.id);
      setProducts(active?.referral_products || []);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/admin/points-config", { points: cfg.points, caps: cfg.caps });
      if (cohortId) {
        await api.patch(`/admin/cohorts/${cohortId}`, { referral_products: products });
      }
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!cfg) return <div className="text-zinc-500">Loading…</div>;
  if (!cfg.hasCohort) return <p className="text-zinc-500">No active cohort. Activate one to edit points.</p>;

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
          <h3 className="font-display text-lg font-bold">Points per activity</h3>
          <div className="mt-4 space-y-3" data-testid="points-grid">
            {CT.map(([k, l]) => (
              <div key={k} className="grid grid-cols-3 items-center gap-3">
                <label className="col-span-2 text-sm text-zinc-300">{l}</label>
                <input type="number" min={0} value={cfg.points[k] ?? 0} onChange={(e) => setCfg({ ...cfg, points: { ...cfg.points, [k]: Number(e.target.value) } })} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" data-testid={`points-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
          <h3 className="font-display text-lg font-bold">Weekly caps</h3>
          <div className="mt-4 space-y-3">
            {CT.map(([k, l]) => (
              <div key={k} className="grid grid-cols-3 items-center gap-3">
                <label className="col-span-2 text-sm text-zinc-300">{l}</label>
                <input type="number" min={0} value={cfg.caps[k] ?? 0} onChange={(e) => setCfg({ ...cfg, caps: { ...cfg.caps, [k]: Number(e.target.value) } })} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" data-testid={`caps-${k}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-bold">Dynamic weekly caps</h3>
            <p className="mt-1 text-xs text-zinc-500 max-w-xl">
              When enabled, each ambassador&apos;s weekly cap is computed at runtime: the budget below is split evenly across the content types whose platform they&apos;ve filled. Minimum 1 per type. Static weekly caps above are ignored.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="dynamic-caps-toggle"
              checked={!!cfg.points.dynamic_caps_enabled}
              onChange={(e) => setCfg({ ...cfg, points: { ...cfg.points, dynamic_caps_enabled: e.target.checked } })}
              className="h-4 w-4 accent-[#A855F7]"
            />
            <span className="font-bold text-zinc-200">Enabled</span>
          </label>
        </div>
        <div className="mt-4">
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-500">Weekly submissions budget per ambassador</label>
          <input
            type="number" min={1} max={100}
            data-testid="weekly-caps-budget"
            value={cfg.points.weekly_caps_budget ?? 10}
            onChange={(e) => setCfg({ ...cfg, points: { ...cfg.points, weekly_caps_budget: Number(e.target.value) } })}
            className="w-32 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm disabled:opacity-50"
            disabled={!cfg.points.dynamic_caps_enabled}
          />
          <p className="mt-1 text-xs text-zinc-500">
            Example: budget=10, ambassador has 2 platforms (Instagram + LinkedIn = 5 content types) → cap=2 per type. Same ambassador with 6 platforms → cap=1 per type.
          </p>
        </div>
      </div>

      <TrackClassificationCard cfg={cfg} setCfg={setCfg} />

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6" data-testid="lb-reveal-card">
        <h3 className="font-display text-lg font-bold">Leaderboard reveal gates</h3>
        <p className="mt-1 text-xs text-zinc-500 max-w-xl">
          A board full of zeros looks dead and discourages new members. Set how many ambassadors must score (points &gt; 0) before each board unlocks. Until then, viewers see a &quot;warming up&quot; teaser instead of a list of 0-point members.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-500">Public leaderboard reveals after</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={1} max={100}
                data-testid="public-reveal-threshold"
                value={cfg.points.public_reveal_threshold ?? 3}
                onChange={(e) => setCfg({ ...cfg, points: { ...cfg.points, public_reveal_threshold: Number(e.target.value) } })}
                className="w-24 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
              />
              <span className="text-xs text-zinc-500">scorers</span>
            </div>
            <p className="mt-1 text-xs text-zinc-500">Public /leaderboard page.</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-500">Dashboard leaderboard reveals after</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={1} max={100}
                data-testid="dashboard-reveal-threshold"
                value={cfg.points.dashboard_reveal_threshold ?? 5}
                onChange={(e) => setCfg({ ...cfg, points: { ...cfg.points, dashboard_reveal_threshold: Number(e.target.value) } })}
                className="w-24 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
              />
              <span className="text-xs text-zinc-500">scorers</span>
            </div>
            <p className="mt-1 text-xs text-zinc-500">Logged-in ambassador board.</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-lg font-bold">Streak bonuses</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ["streak_bonus_weekly", "Weekly streak bonus"],
            ["streak_threshold_weeks", "Streak threshold (weeks)"],
          ].map(([k, l]) => (
            <div key={k}>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-500">{l}</label>
              <input type="number" min={0} value={cfg.points[k] ?? 0} onChange={(e) => setCfg({ ...cfg, points: { ...cfg.points, [k]: Number(e.target.value) } })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" data-testid={`bonus-${k}`} />
            </div>
          ))}
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-500">Referral cap / week</label>
            <input type="number" min={0} value={cfg.caps.referral ?? 9999} onChange={(e) => setCfg({ ...cfg, caps: { ...cfg.caps, referral: Number(e.target.value) } })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
            <p className="mt-1 text-xs text-zinc-500">9999 = unlimited</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Referral products</h3>
          <p className="text-xs text-zinc-500">Per-product signup &amp; conversion points</p>
        </div>
        <div className="mt-4">
          <ReferralProductsEditor value={products} onChange={setProducts} />
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} className="rounded-md bg-[#A855F7] px-5 py-2 text-sm font-bold text-white" data-testid="points-save">Save all changes</button>
      </div>
    </div>
  );
}

function ResourcesTab() {
  return <AdminResourcesTab />;
}

const TYPE_LABELS = {
  student: "Student", graduate: "Graduate", working_professional: "Professional",
  entrepreneur: "Entrepreneur", creator: "Creator", brand: "Brand", business: "Business",
  company: "Company", other: "Other",
};
const TYPE_COLORS = {
  student: "#60A5FA", graduate: "#34D399", working_professional: "#FBBF24",
  entrepreneur: "#F472B6", creator: "#A855F7", brand: "#FB923C", business: "#FB923C",
  company: "#FB923C", other: "#71717A",
};
const typeLabel = (t) => TYPE_LABELS[(t || "").toLowerCase()] || (t ? t.replace(/_/g, " ") : "—");
const typeColor = (t) => TYPE_COLORS[(t || "").toLowerCase()] || "#71717A";
const orgOf = (a) => a.company_name || a.brand_name || a.college || a.content_niche || a.role_title || "—";

function LeaderboardAdminTab() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/ambassadors").then((r) => setItems((r.data.items || []).sort((a, b) => (b.total_points || 0) - (a.total_points || 0)))); }, []);
  if (!items) return <div className="text-zinc-500">Loading…</div>;

  const ranked = items.map((a, i) => ({ ...a, rank: i + 1 }));
  const ql = q.trim().toLowerCase();
  const rows = ql
    ? ranked.filter((a) => [a.full_name, a.email, orgOf(a), typeLabel(a.self_description), a.referral_code].join(" ").toLowerCase().includes(ql))
    : ranked;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold">Leaderboard</h2>
          <p className="mt-0.5 text-xs text-zinc-500">{items.length} active ambassador{items.length === 1 ? "" : "s"} · admin view shows full identity (no anonymity)</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, org, type, email…" className="w-56 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs" data-testid="lb-search" />
          <a href={`${API}/admin/leaderboard/export.csv`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-3 py-2 text-xs hover:border-zinc-700" data-testid="lb-export"><Download className="h-3 w-3" /> Export CSV</a>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-zinc-900 bg-zinc-950">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-900 text-xs uppercase tracking-wider text-zinc-500"><tr>
            <th className="p-3 text-left">Rank</th>
            <th className="p-3 text-left">Name</th>
            <th className="p-3 text-left">Type</th>
            <th className="p-3 text-left">Org / Detail</th>
            <th className="p-3 text-left">Email</th>
            <th className="p-3 text-left">Code</th>
            <th className="p-3 text-right">Refs</th>
            <th className="p-3 text-right">Posts</th>
            <th className="p-3 text-right">Points</th>
          </tr></thead>
          <tbody data-testid="lb-admin-rows">
            {rows.map((a) => {
              const c = typeColor(a.self_description);
              return (
                <tr key={a.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                  <td className="p-3 font-mono text-zinc-400">#{a.rank}</td>
                  <td className="p-3 font-display font-semibold text-white">{a.full_name || "—"}</td>
                  <td className="p-3"><span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: c, backgroundColor: `${c}1f` }}>{typeLabel(a.self_description)}</span></td>
                  <td className="p-3 text-zinc-300">{orgOf(a)}</td>
                  <td className="p-3 text-xs text-zinc-500">{a.email}</td>
                  <td className="p-3 font-mono text-xs text-zinc-500">{a.referral_code}</td>
                  <td className="p-3 text-right font-mono">{a.referral_count ?? 0}</td>
                  <td className="p-3 text-right font-mono text-zinc-400">{a.submission_count ?? 0}</td>
                  <td className="p-3 text-right font-mono font-bold text-[#A855F7]">{a.total_points ?? 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-6 text-center text-zinc-500">{ql ? "No matches." : "No ambassadors yet."}</p>}
      </div>
    </div>
  );
}

function RewardsTab() {
  const [ambs, setAmbs] = useState([]);
  const [form, setForm] = useState({ first: { ambassador_id: "", reward: "" }, second: { ambassador_id: "", reward: "" }, third: { ambassador_id: "", reward: "" } });

  useEffect(() => {
    api.get("/admin/ambassadors").then((r) => setAmbs(r.data.items.sort((a, b) => b.total_points - a.total_points)));
  }, []);

  const announce = async () => {
    if (!form.first.ambassador_id || !form.second.ambassador_id || !form.third.ambassador_id) {
      toast.error("Pick all 3 winners");
      return;
    }
    if (!window.confirm("This will email all ambassadors and mark the cohort completed. Continue?")) return;
    try {
      await api.post("/admin/rewards/announce", form);
      toast.success("Winners announced 🏆");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const renderSlot = (pos, label, color) => (
    <div className="rounded-2xl border bg-zinc-950 p-5" style={{ borderColor: `${color}66`, boxShadow: `0 0 30px ${color}1f` }}>
      <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color }}>{label}</p>
      <Select value={form[pos].ambassador_id} onValueChange={(v) => setForm({ ...form, [pos]: { ...form[pos], ambassador_id: v } })}>
        <SelectTrigger className="mt-3 border-zinc-800 bg-zinc-900" data-testid={`rewards-${pos}-amb`}><SelectValue placeholder="Pick ambassador" /></SelectTrigger>
        <SelectContent>
          {ambs.map((a) => <SelectItem key={a.id} value={a.id}>{a.full_name} — {a.total_points} pts</SelectItem>)}
        </SelectContent>
      </Select>
      <input data-testid={`rewards-${pos}-reward`} placeholder="Reward description" value={form[pos].reward} onChange={(e) => setForm({ ...form, [pos]: { ...form[pos], reward: e.target.value } })} className="mt-3 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
    </div>
  );

  return (
    <div>
      <h2 className="font-display text-2xl font-bold mb-2">Rewards & winners</h2>
      <p className="mb-5 text-sm text-zinc-500">Pick the top 3 and announce. This action is permanent and emails all ambassadors.</p>
      <div className="grid gap-4 md:grid-cols-3">
        {renderSlot("first", "🥇 1st place", "#FFD700")}
        {renderSlot("second", "🥈 2nd place", "#C0C0C0")}
        {renderSlot("third", "🥉 3rd place", "#CD7F32")}
      </div>
      <button onClick={announce} data-testid="rewards-announce-btn" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#A855F7] px-6 py-3 text-sm font-bold text-white hover:-translate-y-0.5 transition-all">
        <Trophy className="h-4 w-4" />Announce Top 3
      </button>
    </div>
  );
}

function TrackClassificationCard({ cfg, setCfg }) {
  const ALL_TYPES = [
    "instagram_post_original", "instagram_story_reshare", "linkedin_post_original",
    "linkedin_reshare", "twitter_post_original", "twitter_reshare", "threads_post_original",
    "whatsapp_share", "instagram_reel", "youtube_video",
    "ai_marketing_video", "newsletter_mention", "blog_post", "podcast_feature",
  ];
  const core = new Set(cfg.points.core_types || []);
  const creator = new Set(cfg.points.creator_types || []);

  const setTrack = (type, track) => {
    const newCore = new Set(core); const newCreator = new Set(creator);
    newCore.delete(type); newCreator.delete(type);
    if (track === "core") newCore.add(type);
    if (track === "creator") newCreator.add(type);
    setCfg({ ...cfg, points: { ...cfg.points, core_types: [...newCore], creator_types: [...newCreator] } });
  };

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6" data-testid="track-classification">
      <h3 className="font-display text-lg font-bold">Activity track classification</h3>
      <p className="mt-1 text-xs text-zinc-500 max-w-xl">
        Decides where each activity tile lands on the ambassador dashboard.
        <span className="text-emerald-300"> Core</span> = the recommended-for-everyone block.
        <span className="text-[#A855F7]"> Creator</span> = the optional high-value block.
        <span className="text-zinc-400"> Other</span> = neither (still shown, just ungrouped).
      </p>
      <div className="mt-4 overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] font-bold uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Content type</th>
              <th className="px-3 py-2 text-center text-emerald-300">Core</th>
              <th className="px-3 py-2 text-center text-[#A855F7]">Creator</th>
              <th className="px-3 py-2 text-center text-zinc-500">Other</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {ALL_TYPES.map((t) => {
              const track = core.has(t) ? "core" : creator.has(t) ? "creator" : "other";
              return (
                <tr key={t} data-testid={`track-row-${t}`}>
                  <td className="px-3 py-2 capitalize text-zinc-300">{t.replace(/_/g, " ")}</td>
                  {["core", "creator", "other"].map((opt) => (
                    <td key={opt} className="px-3 py-2 text-center">
                      <input
                        type="radio"
                        name={`track-${t}`}
                        checked={track === opt}
                        onChange={() => setTrack(t, opt)}
                        data-testid={`track-${t}-${opt}`}
                        className={`h-4 w-4 ${opt === "core" ? "accent-emerald-500" : opt === "creator" ? "accent-[#A855F7]" : "accent-zinc-500"}`}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IntegrationsTab() {
  const [cfg, setCfg] = useState(null);
  const [showKey, setShowKey] = useState(false);

  const load = () => api.get("/admin/integrations").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);

  const saveEnv = async (env) => {
    try {
      const r = await api.put("/admin/integrations", { environment: env });
      setCfg(r.data);
      toast.success(`Switched to ${env.toUpperCase()}`);
    } catch (e) { toast.error("Failed"); }
  };

  const saveUrls = async () => {
    try {
      const r = await api.put("/admin/integrations", { prod_url: cfg.prod_url, test_url: cfg.test_url });
      setCfg(r.data);
      toast.success("URLs updated");
    } catch (e) { toast.error("Failed"); }
  };

  const saveMaxUpload = async () => {
    const mb = parseInt(cfg.max_upload_mb, 10);
    if (!mb || mb < 1 || mb > 500) return toast.error("Enter a limit between 1 and 500 MB");
    try {
      const r = await api.put("/admin/integrations", { max_upload_mb: mb });
      setCfg(r.data);
      toast.success(`Upload limit set to ${r.data.max_upload_mb} MB`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const regenerate = async () => {
    if (!window.confirm("This will invalidate the current API key. IdeaThon will need the new key. Continue?")) return;
    try {
      const r = await api.post("/admin/integrations/regenerate-key");
      setCfg({ ...cfg, webhook_api_key: r.data.webhook_api_key });
      toast.success("New key generated — update IdeaThon");
    } catch (e) { toast.error("Failed"); }
  };

  const togglePublicPoints = async () => {
    const next = !(cfg.show_points_public !== false);
    try {
      const r = await api.put("/admin/integrations/public-points", { enabled: next });
      setCfg({ ...cfg, show_points_public: r.data.show_points_public });
      toast.success(next ? "Point numbers now shown on public boards" : "Point numbers now hidden on public boards");
    } catch (e) { toast.error("Failed"); }
  };

  if (!cfg) return <div className="text-zinc-500">Loading…</div>;

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const snippet = `// On user registration (with ?ref=NSA-... in URL):
fetch("${apiBase}/referrals/track", {
  method: "POST",
  headers: { "Content-Type": "application/json", "X-API-Key": "${cfg.webhook_api_key}" },
  body: JSON.stringify({
    referral_code: refCode,
    product_key: "ideathon",
    referred_email: user.email,
    referred_user_id: user.id,
  }),
});

// On payment CONFIRMED (Stripe payment_intent.succeeded webhook ONLY — never before):
fetch("${apiBase}/referrals/convert", {
  method: "POST",
  headers: { "Content-Type": "application/json", "X-API-Key": "${cfg.webhook_api_key}" },
  body: JSON.stringify({
    product_key: "ideathon",
    referred_email: user.email,
    referred_user_id: user.id,
    payment_reference: stripePaymentIntent.id, // REQUIRED — proof of payment
    amount: stripePaymentIntent.amount / 100,
    currency: stripePaymentIntent.currency.toUpperCase(),
  }),
});`;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-xl font-bold">IdeaThon environment</h3>
        <p className="mt-1 text-sm text-zinc-500">Switch which URL ambassadors' referral links point to. Affects new welcome emails and dashboard links.</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {[["prod", "Production", cfg.prod_url], ["test", "Test / staging", cfg.test_url]].map(([env, label, url]) => (
            <button key={env} data-testid={`env-${env}`} onClick={() => saveEnv(env)} className={`rounded-xl border p-4 text-left transition-all ${cfg.environment === env ? "border-[#A855F7] bg-[#A855F7]/10" : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"}`}>
              <div className="flex items-center justify-between">
                <span className="font-display text-base font-bold">{label}</span>
                {cfg.environment === env && <span className="rounded bg-[#A855F7] px-2 py-0.5 text-[10px] font-bold uppercase text-white">Active</span>}
              </div>
              <p className="mt-2 break-all font-mono text-xs text-zinc-400">{url}</p>
            </button>
          ))}
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-500">Production URL</label>
            <input data-testid="prod-url-input" value={cfg.prod_url || ""} onChange={(e) => setCfg({ ...cfg, prod_url: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-500">Test URL</label>
            <input data-testid="test-url-input" value={cfg.test_url || ""} onChange={(e) => setCfg({ ...cfg, test_url: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
          </div>
        </div>
        <button data-testid="save-urls-btn" onClick={saveUrls} className="mt-4 rounded-md bg-[#A855F7] px-5 py-2 text-sm font-bold text-white">Save URLs</button>
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-xl font-bold">Resource upload limit</h3>
        <p className="mt-1 text-sm text-zinc-500">Maximum size for a single resource file (videos, images, PDFs). Large files upload in chunks so they don't fail at the CDN. Bump this temporarily for a big video, then set it back to 25 MB.</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-500">Max per-file size (MB)</label>
            <input
              data-testid="max-upload-input"
              type="number"
              min="1"
              max="500"
              value={cfg.max_upload_mb ?? 25}
              onChange={(e) => setCfg({ ...cfg, max_upload_mb: e.target.value })}
              className="w-32 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
            />
          </div>
          <button data-testid="save-max-upload-btn" onClick={saveMaxUpload} className="rounded-md bg-[#A855F7] px-5 py-2 text-sm font-bold text-white">Save limit</button>
          <button data-testid="reset-max-upload-btn" onClick={() => setCfg({ ...cfg, max_upload_mb: 25 })} className="rounded-md border border-zinc-800 px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200">Reset to 25</button>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-xl font-bold">Public leaderboard points</h3>
        <p className="mt-1 text-sm text-zinc-500">Controls whether the actual point <b>numbers</b> show on public boards (the landing "Live leaderboard" widget and the /leaderboard page). When off, the public still sees ranks + names, just not the scores. Your dashboard and admin views are unaffected.</p>
        <label className="mt-4 inline-flex cursor-pointer items-center gap-3" data-testid="public-points-toggle-label">
          <input
            type="checkbox"
            data-testid="public-points-toggle"
            checked={cfg.show_points_public !== false}
            onChange={togglePublicPoints}
            className="h-5 w-5 accent-[#A855F7]"
          />
          <span className="text-sm text-zinc-300">{cfg.show_points_public !== false ? "✅ Showing point numbers on public boards" : "🚫 Hiding point numbers on public boards"}</span>
        </label>
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-xl font-bold">Signup points policy</h3>
        <p className="mt-1 text-sm text-zinc-500">By default ambassadors earn points <b>only on paid conversions</b> — not on signups. This prevents bots/spam inflating the leaderboard. Toggle below to enable signup points for specific platforms.</p>
        <div className="mt-4 space-y-2" data-testid="signup-platforms">
          {["ideathon", "academy", "vokyn"].map((p) => {
            const enabled = (cfg.signup_points_enabled_platforms || []).includes(p);
            const toggle = async () => {
              const next = enabled
                ? cfg.signup_points_enabled_platforms.filter((x) => x !== p)
                : [...(cfg.signup_points_enabled_platforms || []), p];
              try {
                const r = await api.put("/admin/integrations", { signup_points_enabled_platforms: next });
                setCfg(r.data);
                toast.success(`${p}: signup points ${enabled ? "disabled" : "enabled"}`);
              } catch (e) { toast.error("Failed"); }
            };
            return (
              <label key={p} className="flex items-center justify-between rounded border border-zinc-800 bg-zinc-900 px-4 py-3 cursor-pointer hover:border-zinc-700" data-testid={`signup-toggle-${p}`}>
                <div>
                  <p className="font-display font-semibold capitalize">{p}</p>
                  <p className="text-xs text-zinc-500">{enabled ? "✅ Signups award referral points" : "🚫 Only paid conversions award points"}</p>
                </div>
                <input type="checkbox" checked={enabled} onChange={toggle} className="h-5 w-5 accent-[#A855F7]" />
              </label>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl font-bold">Webhook API key</h3>
            <p className="mt-1 text-sm text-zinc-500">IdeaThon must include this in the <code className="rounded bg-zinc-900 px-1.5 py-0.5 text-[#A855F7]">X-API-Key</code> header.</p>
          </div>
          <button data-testid="regen-key-btn" onClick={regenerate} className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 px-3 py-2 text-xs text-rose-300 hover:bg-rose-500/10">
            <RefreshCw className="h-3 w-3" /> Regenerate
          </button>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <input data-testid="api-key-input" type={showKey ? "text" : "password"} readOnly value={cfg.webhook_api_key || ""} className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-xs" />
          <button data-testid="toggle-key" onClick={() => setShowKey(!showKey)} className="rounded border border-zinc-800 px-3 py-2 text-xs">{showKey ? "Hide" : "Show"}</button>
          <button data-testid="copy-key" onClick={() => { navigator.clipboard.writeText(cfg.webhook_api_key); toast.success("Copied"); }} className="rounded border border-zinc-800 px-3 py-2 text-xs">Copy</button>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-xl font-bold">IdeaThon integration snippet</h3>
        <p className="mt-1 text-sm text-zinc-500">Paste in IdeaThon backend. Already pre-filled with active URL and key.</p>
        <pre className="mt-4 max-h-[400px] overflow-auto rounded-md border border-zinc-800 bg-black p-4 font-mono text-xs text-zinc-300">{snippet}</pre>
        <button onClick={() => { navigator.clipboard.writeText(snippet); toast.success("Snippet copied"); }} className="mt-3 rounded-md bg-[#A855F7] px-4 py-2 text-xs font-bold text-white" data-testid="copy-snippet">Copy snippet</button>
      </div>

      <ManualAttribution />

      <EmailDeliveryCard />

      <WebhookEventsLog />

      <SocialLinksCard cfg={cfg} reload={load} />

      <MaintenanceCard cfg={cfg} reload={load} />

      <DangerZoneCard />

      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
        <h3 className="font-display text-base font-bold text-amber-200">⚠ What IdeaThon needs</h3>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-zinc-300">
          <li>Accept the <code className="text-[#A855F7]">?ref=</code> URL parameter and store it in localStorage for the session.</li>
          <li>On user registration: POST to <code className="text-[#A855F7]">/api/referrals/track</code> with the stored ref code + user email/id + <code className="text-[#A855F7]">X-API-Key</code> header.</li>
          <li>On payment confirmed (Stripe webhook): POST to <code className="text-[#A855F7]">/api/referrals/convert</code> with the same email/id + a <code className="text-[#A855F7]">payment_reference</code> (their Stripe payment_intent or order ID) + <code className="text-[#A855F7]">X-API-Key</code> header. The payment_reference is stored and shown in the audit log so we can prove every +200 conversion is tied to a real payment.</li>
          <li>Both endpoints are idempotent — duplicates don&apos;t double-credit.</li>
        </ol>
      </div>
    </div>
  );
}

function EmailDeliveryCard() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const loadCount = () => api.get("/admin/emails/failed-welcome-count").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { loadCount(); }, []);

  const count = data?.count ?? null;
  const recipients = data?.recipients || [];

  const resend = async () => {
    if (!window.confirm(`Resend welcome emails to ${count} active ambassador(s) whose welcome email previously failed? Anyone who already got one is skipped.`)) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/emails/resend-failed-welcome");
      setResult(r.data);
      toast.success(`Resent: ${r.data.sent} sent · ${r.data.failed} failed · ${r.data.skipped} skipped`);
      loadCount();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend");
    } finally {
      setRunning(false);
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || "—"; } };

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6" data-testid="email-delivery-card">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-xl font-bold">Email delivery</h3>
        <button onClick={loadCount} className="inline-flex items-center gap-1 rounded border border-zinc-800 px-2.5 py-1.5 text-xs text-zinc-400 hover:border-zinc-700" data-testid="refresh-failed-welcome"><RefreshCw className="h-3 w-3" /> Refresh</button>
      </div>
      <p className="mt-1 text-sm text-zinc-500">
        Welcome emails that failed to send (e.g. during an email-provider outage). Review the list below, then resend.
        Already-delivered ambassadors are skipped, so this is safe to run.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span data-testid="failed-welcome-count" className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-200">
          {count === null ? "…" : `${count}`} to resend
        </span>
        <button
          onClick={resend}
          disabled={running || count === 0}
          data-testid="resend-welcome-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white hover:bg-[#9333EA] disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} /> {running ? "Sending…" : "Resend failed welcome emails"}
        </button>
      </div>

      {recipients.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-lg border border-zinc-800" data-testid="failed-welcome-list">
          <table className="w-full text-left text-xs">
            <thead className="bg-zinc-900/70 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="p-2.5">Recipient</th>
                <th className="p-2.5">Last failed</th>
                <th className="p-2.5">Reason</th>
                <th className="p-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {recipients.map((r, i) => (
                <tr key={i} data-testid={`failed-welcome-row-${i}`} className="text-zinc-300">
                  <td className="p-2.5">
                    <div className="font-semibold text-zinc-100">{r.name || "—"}</div>
                    <div className="font-mono text-[11px] text-zinc-500">{r.email}</div>
                  </td>
                  <td className="whitespace-nowrap p-2.5 text-zinc-400">{fmt(r.last_attempt)}</td>
                  <td className="max-w-[240px] truncate p-2.5 text-rose-300/80" title={r.last_error}>{r.last_error || "—"}</td>
                  <td className="p-2.5">
                    {r.will_resend
                      ? <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-300">Will resend</span>
                      : <span className="rounded bg-zinc-700/40 px-1.5 py-0.5 text-[10px] font-bold uppercase text-zinc-400" title="No active ambassador record">Skipped</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && recipients.length === 0 && (
        <p className="mt-4 text-sm text-emerald-400/80" data-testid="no-failed-welcome">✓ No failed welcome emails — everyone's been delivered.</p>
      )}

      {result && (
        <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-900/60 p-3 text-xs" data-testid="resend-result">
          <p className="text-zinc-300">Attempted <b>{result.attempted}</b> · Sent <b className="text-emerald-400">{result.sent}</b> · Failed <b className="text-rose-400">{result.failed}</b> · Skipped <b>{result.skipped}</b></p>
          {(result.details || []).length > 0 && (
            <ul className="mt-2 max-h-48 space-y-0.5 overflow-auto font-mono text-[11px] text-zinc-400">
              {result.details.map((d, i) => (<li key={i}>{d.email} — {d.status}</li>))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ManualAttribution() {
  const [form, setForm] = useState({ referrer_code: "", referred_email: "", product_key: "brand-ambassador-program", note: "" });
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/referral-products")
      .then((r) => setProducts(r.data.products || r.data.items || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const code = form.referrer_code.trim().toUpperCase();
    const email = form.referred_email.trim().toLowerCase();
    if (!code || !email) return toast.error("Referrer code and referred email are required");
    setSubmitting(true);
    setResult(null);
    try {
      const r = await api.post("/admin/referrals/attribute", {
        referrer_code: code,
        referred_email: email,
        product_key: form.product_key,
        note: form.note,
      });
      setResult(r.data);
      if (r.data.status === "credited") {
        toast.success(`Credited +${r.data.points_awarded} to ${code}`);
      } else {
        toast.success("Queued — will credit automatically when this person is accepted");
      }
      setForm((f) => ({ ...f, referred_email: "", note: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attribution failed");
      setResult(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#A855F7]/30 bg-[#A855F7]/[0.04] p-6" data-testid="manual-attribution-card">
      <h3 className="font-display text-xl font-bold text-[#A855F7]">Manually credit a referral</h3>
      <p className="mt-1 text-xs text-zinc-400">
        Fix historical or missed referrals — enter who referred (their <code className="text-[#A855F7]">NSA-</code> code) and the person they referred (email).
        If that person is already an active ambassador, points are credited instantly; otherwise it&apos;s queued and auto-credits when their application is accepted.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Referrer code</label>
          <input
            data-testid="attr-referrer-code"
            value={form.referrer_code}
            onChange={(e) => set("referrer_code", e.target.value)}
            placeholder="NSA-XXXX-0000"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-sm uppercase"
          />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Referred person&apos;s email</label>
          <input
            data-testid="attr-referred-email"
            value={form.referred_email}
            onChange={(e) => set("referred_email", e.target.value)}
            placeholder="recruit@example.com"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Product</label>
          <select
            data-testid="attr-product"
            value={form.product_key}
            onChange={(e) => set("product_key", e.target.value)}
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
          >
            {(products.length ? products : [{ product_key: "brand-ambassador-program", product_name: "Brand Ambassador Program" }]).map((p) => (
              <option key={p.product_key} value={p.product_key}>{p.product_name || p.name || p.product_key}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Note (optional)</label>
          <input
            data-testid="attr-note"
            value={form.note}
            onChange={(e) => set("note", e.target.value)}
            placeholder="e.g. confirmed by ambassador on WhatsApp"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
          />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          data-testid="attr-submit"
          onClick={submit}
          disabled={submitting}
          className="rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white hover:bg-[#9333EA] disabled:opacity-60"
        >
          {submitting ? "Crediting…" : "Credit referral"}
        </button>
        {result && (
          <span data-testid="attr-result" className={`text-sm font-semibold ${result.status === "credited" ? "text-emerald-300" : "text-amber-300"}`}>
            {result.status === "credited"
              ? `✓ Credited +${result.points_awarded} pts to ${result.referral_code}`
              : "✓ Queued — will credit on acceptance"}
          </span>
        )}
      </div>
    </div>
  );
}

function WebhookEventsLog() {
  const [items, setItems] = useState([]);
  const [filterKind, setFilterKind] = useState("");
  const [filterRef, setFilterRef] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterKind) params.kind = filterKind;
      if (filterRef) params.referral_code = filterRef;
      const r = await api.get("/admin/webhook-events", { params, paramsSerializer: (p) => Object.entries(p).filter(([_, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&") });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterKind]);

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6" data-testid="webhook-events-log">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl font-bold">Webhook events log</h3>
          <p className="mt-1 text-xs text-zinc-500">Every <code className="text-[#A855F7]">/track</code> and <code className="text-[#A855F7]">/convert</code> call is logged here for audit. Look here to verify IdeaThon (or anyone) is calling our endpoints.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="wh-filter-kind"
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            className="rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs"
          >
            <option value="">All kinds</option>
            <option value="track">track (signup)</option>
            <option value="convert">convert (paid)</option>
          </select>
          <input
            data-testid="wh-filter-ref"
            value={filterRef}
            onChange={(e) => setFilterRef(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="filter NSA-…"
            className="w-32 rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs font-mono"
          />
          <button data-testid="wh-refresh" onClick={load} disabled={loading} className="inline-flex items-center gap-1 rounded border border-zinc-800 px-2.5 py-1.5 text-xs text-zinc-300 hover:border-zinc-700 disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-xs">
          <thead className="bg-zinc-900/60 text-[10px] font-bold uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Referral code</th>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-left">Referred email</th>
              <th className="px-3 py-2 text-left">Payment ref</th>
              <th className="px-3 py-2 text-left">Outcome</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900" data-testid="wh-rows">
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500">No webhook calls yet. If IdeaThon refers someone, it&apos;ll appear here.</td></tr>
            )}
            {items.map((e) => {
              const ok = e.outcome?.ok;
              const pts = e.outcome?.points_awarded ?? e.outcome?.bonus_awarded ?? 0;
              const payRef = e.payload?.payment_reference;
              return (
                <tr key={e.id} data-testid={`wh-row-${e.id}`} className="hover:bg-zinc-900/40">
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400 whitespace-nowrap">{new Date(e.at).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${e.kind === "convert" ? "bg-emerald-500/15 text-emerald-300" : "bg-[#A855F7]/15 text-[#A855F7]"}`}>{e.kind}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">{e.referral_code || "—"}</td>
                  <td className="px-3 py-2 text-zinc-400">{e.product_key || "—"}</td>
                  <td className="px-3 py-2 text-zinc-400">{e.referred_email || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">
                    {payRef ? <span className="text-emerald-300">{payRef}</span> : (e.kind === "convert" ? <span className="text-rose-300" title="No payment_reference passed — bonus awarded blindly. Ask IdeaThon to include their Stripe payment_intent.">⚠ none</span> : "—")}
                  </td>
                  <td className="px-3 py-2">
                    {ok ? (
                      <span className="text-emerald-300">
                        ✓ ok{pts ? ` · +${pts} pts` : ""}{e.outcome?.duplicate ? " · duplicate" : ""}{e.outcome?.found === false ? " · no match" : ""}
                      </span>
                    ) : (
                      <span className="text-rose-300">✗ {e.outcome?.error || "failed"}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {items.length > 0 && (
        <p className="mt-2 text-[11px] text-zinc-500">Showing latest {items.length}. Convert rows without a <code className="text-amber-300">payment_reference</code> mean IdeaThon awarded a bonus without sending payment proof — flag this with their team.</p>
      )}
    </div>
  );
}

function AdminsTab() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const load = () =>
    api.get("/admin/admins").then((r) => {
      setItems(r.data.items);
      setCurrent(r.data.current);
    });
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email.trim()) { toast.error("Email required"); return; }
    try {
      await api.post("/admin/admins", { email: email.trim().toLowerCase(), name: name.trim() || null });
      toast.success("Admin added");
      setEmail(""); setName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this admin?")) return;
    try {
      await api.delete(`/admin/admins/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-lg font-bold">Add admin</h3>
        <p className="mt-1 text-sm text-zinc-500">They'll sign in with a magic link — no password needed.</p>
        <div className="mt-4 space-y-3">
          <input data-testid="admin-add-email" type="email" placeholder="admin@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
          <input data-testid="admin-add-name" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
          <button data-testid="admin-add-btn" onClick={add} className="rounded-md bg-[#A855F7] px-5 py-2 text-sm font-bold text-white hover:-translate-y-0.5 transition-all">+ Add admin</button>
        </div>
      </div>
      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-lg font-bold">All admins ({items.length})</h3>
        <ul className="mt-4 space-y-2" data-testid="admin-list">
          {items.map((a) => (
            <li key={a.id} className="flex items-center justify-between rounded border border-zinc-800 bg-zinc-900 p-3 text-sm">
              <div>
                <p className="font-display font-semibold">{a.name}</p>
                <p className="text-xs text-zinc-500">{a.email}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {a.protected && <span className="rounded bg-[#A855F7]/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#A855F7]">Protected (env)</span>}
                  {a.email === current && <span className="rounded bg-zinc-700 px-1.5 py-0.5 text-[10px] font-bold uppercase text-zinc-200">You</span>}
                </div>
              </div>
              {!a.protected && a.email !== current && (
                <button data-testid={`admin-remove-${a.id}`} onClick={() => remove(a.id)} className="rounded border border-rose-500/40 px-2 py-1 text-xs text-rose-300 hover:bg-rose-500/10">Remove</button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function Admin() {
  const user = getUser();
  const nav = useNavigate();
  useEffect(() => { if (!user || user.role !== "admin") nav("/login"); }, []);
  if (!user || user.role !== "admin") return null;
  const logout = async () => { await apiLogout(); nav("/"); };

  // persist active tab in URL hash so refresh keeps the admin on the same tab
  const ADMIN_TABS = ["stats", "cohorts", "apps", "amb", "content", "points", "res", "lb", "rew", "integrations", "admins"];
  const initialTab = (typeof window !== "undefined" && window.location.hash.replace("#", "")) || "stats";
  const safeInitial = ADMIN_TABS.includes(initialTab) ? initialTab : "stats";

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-30 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-5 min-w-0">
            <Logo />
            <span className="hidden text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7] md:inline">Admin</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 text-sm">
            <span className="hidden text-zinc-300 md:inline" data-testid="admin-user">{user.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-2 sm:px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-700" data-testid="admin-logout"><LogOut className="h-3 w-3" /><span className="hidden sm:inline">Logout</span></button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-3 py-5 sm:px-5 sm:py-8">
        <Tabs defaultValue={safeInitial} onValueChange={(v) => { if (typeof window !== "undefined") window.history.replaceState(null, "", `#${v}`); }}>
          <TabsList data-testid="admin-tabs" className="mb-6 flex w-full overflow-x-auto sm:grid sm:grid-cols-11 gap-1 rounded-md border border-zinc-900 bg-zinc-950 p-1 scrollbar-thin">
            <TabsTrigger value="stats" data-testid="t-stats" className="shrink-0 sm:shrink"><LayoutDashboard className="mr-1 h-4 w-4" />Stats</TabsTrigger>
            <TabsTrigger value="cohorts" data-testid="t-cohorts" className="shrink-0 sm:shrink"><Calendar className="mr-1 h-4 w-4" />Cohorts</TabsTrigger>
            <TabsTrigger value="apps" data-testid="t-apps" className="shrink-0 sm:shrink"><Inbox className="mr-1 h-4 w-4" />Apps</TabsTrigger>
            <TabsTrigger value="amb" data-testid="t-amb" className="shrink-0 sm:shrink"><Users className="mr-1 h-4 w-4" />Ambassadors</TabsTrigger>
            <TabsTrigger value="content" data-testid="t-content" className="shrink-0 sm:shrink"><FileText className="mr-1 h-4 w-4" />Content</TabsTrigger>
            <TabsTrigger value="points" data-testid="t-points" className="shrink-0 sm:shrink"><Settings className="mr-1 h-4 w-4" />Points</TabsTrigger>
            <TabsTrigger value="res" data-testid="t-res" className="shrink-0 sm:shrink"><Boxes className="mr-1 h-4 w-4" />Resources</TabsTrigger>
            <TabsTrigger value="lb" data-testid="t-lb" className="shrink-0 sm:shrink"><Trophy className="mr-1 h-4 w-4" />Leaderboard</TabsTrigger>
            <TabsTrigger value="rew" data-testid="t-rew" className="shrink-0 sm:shrink"><Award className="mr-1 h-4 w-4" />Rewards</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="t-integrations" className="shrink-0 sm:shrink"><Plug className="mr-1 h-4 w-4" />Integrations</TabsTrigger>
            <TabsTrigger value="admins" data-testid="t-admins" className="shrink-0 sm:shrink"><Shield className="mr-1 h-4 w-4" />Admins</TabsTrigger>
          </TabsList>
          <TabsContent value="stats"><StatsTab /></TabsContent>
          <TabsContent value="cohorts"><CohortsTab /></TabsContent>
          <TabsContent value="apps"><ApplicationsTab /></TabsContent>
          <TabsContent value="amb"><AmbassadorsTab /></TabsContent>
          <TabsContent value="content"><ContentTab /></TabsContent>
          <TabsContent value="points"><PointsConfigTab /></TabsContent>
          <TabsContent value="res"><ResourcesTab /></TabsContent>
          <TabsContent value="lb"><LeaderboardAdminTab /></TabsContent>
          <TabsContent value="rew"><RewardsTab /></TabsContent>
          <TabsContent value="integrations"><IntegrationsTab /></TabsContent>
          <TabsContent value="admins"><AdminsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function SocialLinksCard({ cfg, reload }) {
  const initial = cfg?.socials || {
    instagram: { url: "", visible: false, show_in_hero: false },
    linkedin: { url: "", visible: false, show_in_hero: false },
    twitter: { url: "", visible: false, show_in_hero: false },
    youtube: { url: "", visible: false, show_in_hero: false },
  };
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(cfg?.socials || initial); /* eslint-disable-next-line */ }, [cfg?.socials]);

  const set = (platform, key, val) => {
    setDraft((d) => ({ ...d, [platform]: { ...d[platform], [key]: val } }));
  };

  const save = async () => {
    // Guard: a channel marked "Visible" with no URL silently won't render on the public footer.
    const missing = Object.entries(draft || {})
      .filter(([, v]) => v?.visible && !(v?.url || "").trim())
      .map(([k]) => k);
    if (missing.length) {
      toast.error(`Add a URL for: ${missing.join(", ")} — "Visible" is on but the link is empty, so it won't show publicly.`);
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/integrations/socials", draft);
      toast.success("Social links saved");
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const PLATFORMS = [
    { k: "instagram", label: "Instagram", placeholder: "https://instagram.com/noobsync" },
    { k: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/noobsync" },
    { k: "twitter", label: "X (Twitter)", placeholder: "https://x.com/noobsync" },
    { k: "youtube", label: "YouTube", placeholder: "https://youtube.com/@noobsync" },
  ];

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6" data-testid="socials-card">
      <h3 className="font-display text-lg font-bold">Social media links</h3>
      <p className="mt-1 text-xs text-zinc-500">
        <span className="font-semibold text-zinc-300">Visible</span> renders the icon in the footer.{" "}
        <span className="font-semibold text-zinc-300">Show on landing hero</span> additionally surfaces it above-the-fold on the landing page.
      </p>
      <div className="mt-4 space-y-3">
        {PLATFORMS.map((p) => {
          const v = draft[p.k] || { url: "", visible: false, show_in_hero: false };
          return (
            <div key={p.k} className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[120px_1fr]">
                <span className="self-center text-sm font-semibold text-zinc-200">{p.label}</span>
                <input
                  value={v.url}
                  onChange={(e) => set(p.k, "url", e.target.value)}
                  placeholder={p.placeholder}
                  data-testid={`social-url-${p.k}`}
                  className="w-full rounded border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#A855F7]"
                />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 pl-0 sm:pl-[120px]">
                <label className="inline-flex items-center gap-2 text-xs text-zinc-400">
                  <input
                    type="checkbox"
                    checked={!!v.visible}
                    onChange={(e) => set(p.k, "visible", e.target.checked)}
                    data-testid={`social-visible-${p.k}`}
                    className="h-4 w-4 accent-[#A855F7]"
                  />
                  <span className={`font-bold uppercase tracking-wider ${v.visible ? "text-emerald-300" : "text-zinc-500"}`}>
                    {v.visible ? "Visible" : "Hidden"}
                  </span>
                </label>
                <label className="inline-flex items-center gap-2 text-xs text-zinc-400">
                  <input
                    type="checkbox"
                    checked={!!v.show_in_hero}
                    disabled={!v.visible}
                    onChange={(e) => set(p.k, "show_in_hero", e.target.checked)}
                    data-testid={`social-hero-${p.k}`}
                    className="h-4 w-4 accent-[#A855F7] disabled:opacity-40"
                  />
                  <span className={`font-bold uppercase tracking-wider ${v.show_in_hero && v.visible ? "text-[#A855F7]" : "text-zinc-500"}`}>
                    Show on landing hero
                  </span>
                </label>
                {v.visible && !(v.url || "").trim() && (
                  <span className="text-xs font-semibold text-amber-400" data-testid={`social-warn-${p.k}`}>
                    ⚠ Add a URL — won't show publicly
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="social-save-btn"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white hover:bg-[#9333EA] disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save social links"}
      </button>
    </div>
  );
}


function MaintenanceCard({ cfg, reload }) {
  const [enabled, setEnabled] = useState(Boolean(cfg?.maintenance_enabled));
  const [message, setMessage] = useState(cfg?.maintenance_message || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(Boolean(cfg?.maintenance_enabled));
    setMessage(cfg?.maintenance_message || "");
  }, [cfg?.maintenance_enabled, cfg?.maintenance_message]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/integrations/maintenance", { enabled, message: message.trim() });
      toast.success(enabled ? "Maintenance mode ENABLED — public site is now offline" : "Maintenance mode disabled — site is back online");
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border p-6 ${enabled ? "border-amber-500/50 bg-amber-500/10" : "border-zinc-900 bg-zinc-950"}`}
      data-testid="maintenance-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-bold">
            🛠 Maintenance mode{" "}
            {enabled && (
              <span className="ml-1 rounded-full border border-amber-400/60 bg-amber-500/20 px-2 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-amber-200">
                ON
              </span>
            )}
          </h3>
          <p className="mt-1 max-w-xl text-xs text-zinc-400">
            When enabled, the public site (Landing, Apply, Dashboard, Leaderboard) shows a maintenance page.
            The admin panel, magic-link login, and legal pages stay reachable so you can flip it back off.
          </p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-3" data-testid="maintenance-toggle-label">
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
            {enabled ? "Site offline" : "Site live"}
          </span>
          <span className="relative inline-flex h-6 w-11 items-center">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="peer sr-only"
              data-testid="maintenance-toggle"
            />
            <span className="block h-6 w-11 rounded-full border border-zinc-700 bg-zinc-800 transition peer-checked:border-amber-400 peer-checked:bg-amber-500" />
            <span className="absolute left-0.5 h-5 w-5 rounded-full bg-white transition peer-checked:translate-x-5" />
          </span>
        </label>
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">
          Optional custom message — shown to visitors during downtime
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="e.g. We're rolling out the Cohort 2 launch — back online by 5pm IST."
          data-testid="maintenance-message"
          className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white outline-none focus:border-amber-400"
        />
        <p className="mt-1 text-[10px] text-zinc-600">{message.length}/500 — leave empty for the default message.</p>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="maintenance-save-btn"
        className={`mt-4 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-bold transition ${
          enabled
            ? "bg-amber-500 text-zinc-950 hover:bg-amber-400"
            : "bg-[#A855F7] text-white hover:bg-[#9333EA]"
        } disabled:opacity-60`}
      >
        {saving ? "Saving…" : enabled ? "Save & take site offline" : "Save"}
      </button>
    </div>
  );
}

function DangerZoneCard() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [wiping, setWiping] = useState(false);

  const wipe = async () => {
    if (confirmText !== "WIPE") {
      toast.error('Type "WIPE" exactly to confirm');
      return;
    }
    setWiping(true);
    try {
      const r = await api.post("/admin/danger/wipe-launch-data", { confirm: "WIPE" });
      const deleted = r.data?.deleted || {};
      const total = Object.values(deleted).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Wiped ${total} records. Reloading…`);
      setOpen(false);
      setConfirmText("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-6" data-testid="danger-zone-card">
      <h3 className="font-display text-lg font-bold text-rose-200">⚠ Danger zone — Launch wipe</h3>
      <p className="mt-2 text-sm text-zinc-300">
        Hard-deletes <strong className="text-white">all operational data</strong> so the platform is launch-ready:
        applications, ambassadors, cohorts, content submissions, referrals, webhook events, email logs, magic tokens, notifications, and admin action history.
      </p>
      <p className="mt-2 text-sm text-emerald-200">
        <strong>Kept</strong>: your admin login, platform settings (webhook key, environment, socials, hero-toggle config), and{" "}
        <strong className="text-emerald-100">🔒 all protected resources</strong> (the standard auto-seeded brand kit — logos, captions, PDFs).
      </p>
      <p className="mt-1 text-xs text-zinc-500">
        Unprotected resources (anything you uploaded manually) are deleted. Brand-kit assets survive by design — you can still hide or manually delete them from the Resources tab.
      </p>
      <p className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
        This action is <strong>irreversible</strong>. There is no undo. Use only when you&apos;re about to launch a fresh cohort to real users.
      </p>
      <button
        onClick={() => setOpen(true)}
        data-testid="danger-wipe-open"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-rose-500/60 bg-rose-500/20 px-4 py-2 text-sm font-bold text-rose-100 hover:bg-rose-500/30"
      >
        🧨 Wipe launch data…
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent className="border-rose-500/40 bg-zinc-950">
          <DialogHeader>
            <DialogTitle className="text-rose-200">Confirm launch wipe</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-300">
              You&apos;re about to permanently delete every application, ambassador, cohort, submission, referral, and webhook event.
              Your admin login, platform settings, and the <strong className="text-emerald-200">🔒 protected brand kit</strong> survive.
            </p>
            <p className="text-sm text-zinc-300">
              Type <code className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-rose-300">WIPE</code> below to enable the button:
            </p>
            <input
              data-testid="danger-wipe-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type WIPE"
              className="w-full rounded-md border border-rose-500/40 bg-zinc-900 px-3 py-2 font-mono text-sm uppercase text-rose-100 outline-none focus:border-rose-500"
            />
          </div>
          <DialogFooter>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-200 hover:border-zinc-500"
            >
              Cancel
            </button>
            <button
              onClick={wipe}
              disabled={wiping || confirmText !== "WIPE"}
              data-testid="danger-wipe-confirm-btn"
              className="rounded-md bg-rose-600 px-5 py-2 text-sm font-bold text-white hover:bg-rose-500 disabled:opacity-50"
            >
              {wiping ? "Wiping…" : "Yes — wipe everything"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


